<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import ConfiguratorOptionGroup from './option-group.vue'
import GroupBildgroesse from './groups/size.vue'
import GroupPlaceholder from './groups/placeholder.vue'
import GroupUpload from './groups/upload.vue'
import LeaveEditorConfirmModal from '../LeaveEditorConfirmModal.vue'
import { useRoute, useRouter } from 'vue-router'

import { useImageDB } from '../../../utils/imageDb'
import { usePriceCalculator } from '../../../composables/usePriceCalculator'
import { useEditorCatalogFlow } from '../../../composables/useEditorCatalogFlow'
import { useCartStore } from '../../../store/cart'
import { useProjectStore } from '../../../store/useProjectStore'
import { useToast } from '../../../composables/useToast'
import { buildMaxCropForRecord } from '../../../utils/cropPreset'
import { geometryHasGuides, normalizeGeometry, type ProductGeometry } from '@pfe/design-renderer'
import { getWidgetConfig } from '../../../runtime/widgetConfig'
import { defaultSizeOption, findMatchingSizeOption, sizeDimsMm } from '../../../utils/sizeMatch'
import { compareSizeOptions } from '../../../utils/sizeSorting'
import { fetchEditorCatalogBootstrap, type EditorCatalogBootstrapResponse } from '../../../services/editorCatalogApi'
import { fetchProjectUploads, invalidateProjectUploadsCache, mapRemoteUploadToRecordMeta } from '../../../services/projectUploadBridge'
import { useTransientUploadStore } from '../../../store/useTransientUploadStore'
import { syncUploadMetaBatch } from '../../../services/uploadMetaApi'
import { ShoppingCart } from 'lucide-vue-next'
import { onUploadEvent } from '../../../utils/uploadBus'

/** Props & Emits */
const props = defineProps<{
  category?: number
  productId?: number
  logoUrl?: string
  /** ✅ Multi: IDs der selektierten Bilder (Source of truth kommt aus Multi-Editor page) */
  selectedIds?: string[]
  /** Upload-Gruppe rechts anzeigen? (Default: true) */
  showUpload?: boolean
  /** Multi-Mode */
  isMultiMode?: boolean
  /** Gesamtanzahl aller Bilder im Editor (für Gesamtpreis-Anzeige) */
  totalImageCount?: number
  /** Unterkategorien der aktuellen Root-Kategorie */
  subcategories?: Array<{
    _id: number | string
    id?: string
    title: string
    description?: string | null
    thumbnail?: string | null
    thumbnailWebp?: string | null
    productCount?: number | null
  }>
  /** Aktive Unterkategorie-ID (wenn man sich in einer Subkat befindet) */
  activeSubcategoryId?: number | null
  /** Eltern-Kategorie-ID (Root), wenn aktuelle Kategorie eine Unterkat ist */
  parentCategoryId?: number | null
  /**
   * Design-Editor: Sidebar schreibt nichts in die Bild-DB, sondern meldet Produkt/Format/
   * Optionen/Menge per `preset-change`. Der Design-Editor haelt das Preset selbst.
   */
  controlled?: boolean
  /** Design-Editor: aktuell gewaehlte Groesse (Size-ID), fuer die Vorbelegung */
  controlledSizeId?: number | null
}>()

const emit = defineEmits<{
  (e: 'changeSize', w: number, h: number): void
  (e: 'changeGroup', name: string | null): void
  (e: 'applied'): void
  (e: 'add-to-cart'): void
  (e: 'db-updated', ids: string[]): void
  (e: 'apply-size-local', payload: { ids: string[]; size: ImageSize }): void
  (e: 'select-subcategory', cat: any): void
  /** Nach Apply: Navigation zur richtigen Kategorie/Produkt-URL */
  (e: 'navigate', payload: { categoryId?: number | null; productId?: number | null }): void
  /** Preis-Update für Topbar-Anzeige */
  (e: 'price-change', label: string): void
  /** Design-Editor (controlled): Produkt/Format/Optionen/Menge geaendert */
  (e: 'preset-change', payload: { productId: number | null; size: ImageSize | null; options: ImageOption[]; quantity: number; grayscale: boolean }): void
  /** Multi: noch nicht übernommene Optionen als Vorschau in den Kacheln (null = Vorschau beenden) */
  (e: 'options-preview', payload: { ids: string[]; options: ImageOption[] } | null): void
}>()

/** Router/Route */
const router = useRouter()
const route = useRoute()

/** DB (Persistenz) – in Multi nur READ + Apply-Writes */
const db = useImageDB('imageDB')
const cart = useCartStore()
const project = useProjectStore()
const transientUploads = useTransientUploadStore()
const toast = useToast()
const EDITOR_IMAGES_CHANGED_EVENT = 'pfe:editor-images-changed'
const hydratingFromDb = ref(false)
const cartInfoModal = ref<{ title: string; body: string; progress: number } | null>(null)
const leaveConfirmModal = ref<{ target: string } | null>(null)

/** Aktives Produkt im Configurator (kann von Auswahl gesteuert werden) */
const selectedProductId = ref<number | null>(props.productId ?? null)
watch(
    () => props.productId,
    (pid) => {
      if (isMulti.value && hasPendingConfigChanges.value) return
      // route/parent updates sollen weiterhin synchronisiert werden
      selectedProductId.value = pid ?? null
    },
)

/** Typen (Preset-Persistenz) */
type ImageSize = {
  title?: string | null
  widthMm?: number
  heightMm?: number
  variantId?: string
  /** Beschnitt/Sicherheitsbereich/Form aus dem Bootstrap (siehe sizeGeometryPayload) */
  bleedMm?: number
  safeMarginMm?: number
  geometry?: ProductGeometry | null
}

/**
 * Geometrie-Anteil eines Size-Payloads aus einer Bootstrap-Größenoption. Ohne Option
 * (individuelle Maße) wird der Beschnitt des Produkts von einer beliebigen Größe des
 * Produkts übernommen (Beschnitt ist praktisch immer produktweit gleich), Form = Rechteck.
 */
function sizeGeometryPayload(opt: any, widthMm?: number, heightMm?: number): Pick<ImageSize, 'bleedMm' | 'safeMarginMm' | 'geometry'> {
  const raw = opt?.geometry ?? null
  const fallback = raw ? null : (sizeOptions.value.find((o: any) => o?.geometry)?.geometry ?? null)
  const source = raw ?? (fallback ? { ...fallback, shape: 'rect', cornerRadiusMm: 0, trimWidthMm: 0, trimHeightMm: 0, fullWidthMm: 0, fullHeightMm: 0 } : null)
  if (!source) return {}
  const geometry = normalizeGeometry(source, { widthMm, heightMm })
  return {
    ...(geometry.bleedMm > 0 ? { bleedMm: geometry.bleedMm } : {}),
    ...(geometry.safeMarginMm > 0 ? { safeMarginMm: geometry.safeMarginMm } : {}),
    ...(geometryHasGuides(geometry) ? { geometry } : {}),
  }
}
type ImageOption = { groupId: string; variantId: string; value: string }

function parseSizeTitle(title: string | null | undefined): { wCm: number; hCm: number; wMm: number; hMm: number; title: string } | null {
  const raw = String(title || '').trim()
  if (!raw) return null
  const match = raw.replace(/cm/ig, '').match(/(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)/i)
  if (!match) return null
  const wCm = Number(match[1].replace(',', '.'))
  const hCm = Number(match[2].replace(',', '.'))
  if (!Number.isFinite(wCm) || !Number.isFinite(hCm) || wCm <= 0 || hCm <= 0) return null
  return {
    wCm,
    hCm,
    wMm: Math.round(wCm * 10),
    hMm: Math.round(hCm * 10),
    title: `${wCm} x ${hCm} cm`,
  }
}

function normId(v: any): string {
  const s = String(v ?? '').trim()
  if (!s) return ''
  if (!s.includes('/')) return s
  const parts = s.split('/').filter(Boolean)
  return parts[parts.length - 1] || s
}

/** State */
const quantity = ref(1)
const grayscale = ref(false)
const selectedFormat = ref<string>('') // Anzeige Size-Gruppe
const selectedPrice = ref<number>(0) // Fallback wenn kein Kalkulator
const selectedImage = ref<string>('Kein Bild')

const activeGroupId = ref<string | null>(null)
const activeComponentKey = ref<'size' | 'upload' | 'placeholder' | null>(null)
const activeGroupTitle = ref<string | null>(null)

/** Flags */
const showUpload = computed<boolean>(() => props.showUpload ?? true)
const isMulti = computed<boolean>(() => {
  if (props.isMultiMode != null) return !!props.isMultiMode
  return route.path === '/multi' || route.path.startsWith('/multi')
})
/** Selection IDs (Multi) */
const selectionIds = computed<string[]>(() => (props.selectedIds ?? []).filter(Boolean))
const singleImageId = computed<string>(() => String(route.query.imageId || '').trim())
const singleUploadReady = ref(false)
const multiUploadReady = ref(false)
const LARGE_MULTI_SELECTION_FAST_PATH = 24
let offUploadBus: null | (() => void) = null
const canAddToCartSingle = computed<boolean>(() => {
  if (isMulti.value) return false
  const pid = Number(activeProductId.value ?? props.productId ?? 0)
  return !!singleImageId.value && pid > 0 && singleUploadReady.value
})
const canSaveSingle = computed<boolean>(() => !isMulti.value && !!singleImageId.value)
const canSaveMulti = computed<boolean>(() => {
  if (!(isMulti.value && selectionIds.value.length > 0)) return false
  if (inlineCustomEnabled.value && allowCustomSize.value && !inlineCustomValid.value) return false
  return true
})
const canAddToCartMulti = computed<boolean>(() => {
  if (!isMulti.value || !selectionIds.value.length) return false
  const pid = Number(activeProductId.value ?? props.productId ?? 0)
  return pid > 0 && multiUploadReady.value
})
const overviewLinkHref = computed<string>(() => String((getWidgetConfig() as any)?.overviewUrl || '/').trim())
const cartLinkHref = computed<string>(() => String((getWidgetConfig() as any)?.cartUrl || '/checkout/cart').trim())
const shippingCostsHref = computed<string>(() => String((getWidgetConfig() as any)?.shippingCostsUrl || '#').trim())
const useLargeMultiSelectionFastPath = computed<boolean>(() => isMulti.value && selectionIds.value.length > LARGE_MULTI_SELECTION_FAST_PATH)

function goToCartLink() {
  const href = cartLinkHref.value
  if (!href) return
  let navigated = false
  const commit = () => {
    if (navigated) return
    navigated = true
    window.location.href = href
  }
  window.setTimeout(commit, 180)
  void cart.primeCheckoutSession().catch(() => {}).finally(commit)
}

function requestLeaveEditor(target?: string | null) {
  leaveConfirmModal.value = {
    target: String(target || overviewLinkHref.value || '/').trim() || '/',
  }
}

function confirmLeaveEditor() {
  const target = String(leaveConfirmModal.value?.target || overviewLinkHref.value || '/').trim() || '/'
  leaveConfirmModal.value = null
  window.location.href = target
}

function isUploadDone(rec: any): boolean {
  const hasRemoteAsset =
    !!String(rec?.upload?.remoteUrl || '').trim() ||
    !!String(rec?.upload?.previewUrl || '').trim() ||
    !!String(rec?.upload?.thumbUrl || '').trim()
  if (!hasRemoteAsset) return false
  const state = String(rec?.upload?.state || '').trim()
  const phase = String(rec?.upload?.phase || '').trim()
  const editorReadyAt = Number(rec?.upload?.editorReadyAt || 0)
  if (state === 'done' || phase === 'done') return true
  return Number.isFinite(editorReadyAt) && editorReadyAt > 0
}

function uploadPatchLooksReady(patch: any): boolean {
  const upload = (patch?.upload && typeof patch.upload === 'object') ? patch.upload : {}
  const state = String(upload?.state || '').trim()
  const phase = String(upload?.phase || '').trim()
  const hasRemoteAsset =
    !!String(upload?.remoteUrl || '').trim() ||
    !!String(upload?.previewUrl || '').trim() ||
    !!String(upload?.thumbUrl || '').trim()
  if (!hasRemoteAsset) return false
  const editorReadyAt = Number(upload?.editorReadyAt || 0)
  if (state === 'done' || phase === 'done') return true
  return Number.isFinite(editorReadyAt) && editorReadyAt > 0
}

function showSingleCartUploadInfo() {
  const id = singleImageId.value
  const scopeText = id ? 'Das Bild wird noch hochgeladen.' : 'Es ist noch kein uploadfertiges Bild verfügbar.'
  cartInfoModal.value = {
    title: 'Warenkorb noch nicht verfügbar',
    body: `${scopeText} Sie können Ihre Konfiguration schon lokal speichern. In den Warenkorb ist das Bild erst möglich, sobald der Upload vollständig abgeschlossen ist.`,
    progress: singleUploadReady.value ? 100 : 0,
  }
}

function openUploadQueueFromDialog() {
  window.dispatchEvent(new CustomEvent('pfe:show-upload-queue'))
  cartInfoModal.value = null
}

async function refreshCartAvailability() {
  if (!isMulti.value) {
    if (!singleImageId.value) {
      singleUploadReady.value = false
      return
    }
    const rec = await db.get(singleImageId.value)
    singleUploadReady.value = isUploadDone(rec)
    return
  }

  if (!selectionIds.value.length) {
    multiUploadReady.value = false
    return
  }

  if (useLargeMultiSelectionFastPath.value) {
    multiUploadReady.value = false
    return
  }

  const rows = await Promise.all(selectionIds.value.map((id) => db.get(id)))
  multiUploadReady.value = rows.length > 0 && rows.every((rec) => isUploadDone(rec))
}

const hoverPreview = ref<{
  title: string
  description: string
  image: string
  x: number
  y: number
} | null>(null)

function previewDescription(item: any): string {
  return String(item?.shortDescription || item?.description || '').trim()
}

function openHoverPreview(event: MouseEvent, item: any, imageResolver: (item: any) => string) {
  const target = event.currentTarget as HTMLElement | null
  if (!target) return
  const rect = target.getBoundingClientRect()
  hoverPreview.value = {
    title: String(item?.titleShort || item?.title || '').trim(),
    description: previewDescription(item),
    image: imageResolver(item),
    x: rect.right + 14,
    y: rect.top + Math.min(28, Math.max(6, rect.height * 0.2)),
  }
}

function closeHoverPreview() {
  hoverPreview.value = null
}


// ✅ Multi UX: wie viele selektierte Bilder gehören NICHT zum aktuellen Produkt?
const selectionMismatchCount = ref<number>(0)

const applyLabel = computed(() => {
  const n = selectionIds.value.length
  if (!n) return 'ÜBERNEHMEN'
  return `ÜBERNEHMEN (${n})`
})

watch(
    () => [isMulti.value, props.productId, selectionIds.value.join('|')],
    async () => {
      if (!isMulti.value) { selectionMismatchCount.value = 0; return }
      selectionMismatchCount.value = 0
    },
    { immediate: true },
)
const sizePreselectTitle = ref<string | null>(null)
const catalogBootstrap = ref<EditorCatalogBootstrapResponse | null>(null)

// Merkt sich die zuletzt im Sidebar-UI gewählte Größe (Multi: nur Preview)
const pendingSizePatch = ref<ImageSize | null>(null)
const selectedOptions = ref<Map<string, { variantId: string; value: string }>>(new Map())
const hasPendingConfigChanges = ref(false)

// Multi-Mode: Welche Felder hat der Nutzer in dieser Sidebar-Sitzung wirklich
// angefasst? Beim Apply werden NUR diese auf alle markierten Bilder geschrieben.
// Vorher wurden Menge, S/W und Optionen immer mitgeschickt – hydriert vom ersten
// Bild der Auswahl (oder auf 1 zurückgesetzt) – und haben damit bei einer reinen
// Größenänderung die individuellen Anzahlen aller anderen Bilder überschrieben.
const quantityTouched = ref(false)
const grayscaleTouched = ref(false)
const optionsTouched = ref(false)
// Nur die tatsächlich geänderten Optionsgruppen übernehmen. Die übrigen Gruppen
// zeigen in der Sidebar oft den Stand eines vorher gewählten Bildes – sie mit zu
// schreiben hat z. B. „Fit-In“ beim Umstellen von „Motivrand“ auf „Fill-In“ gesetzt.
const touchedOptionGroupIds = ref(new Set<string>())
const productTouched = ref(false)
/** Multi: geänderte, noch nicht übernommene Optionsgruppen als Kachel-Vorschau melden. */
function emitOptionsPreview() {
  if (!isMulti.value || !selectionIds.value.length) return
  const options: ImageOption[] = []
  for (const g of cfg.value?.groups ?? []) {
    if (g?.isSize || !g?.id || !touchedOptionGroupIds.value.has(String(g.id))) continue
    const selectedId = String(g?.selected?.id || '')
    if (!selectedId) continue
    options.push({ groupId: String(g.id), variantId: selectedId, value: String(g.selected.title || '') })
  }
  emit('options-preview', options.length ? { ids: [...selectionIds.value], options } : null)
}
function resetMultiDirtyFlags() {
  quantityTouched.value = false
  grayscaleTouched.value = false
  optionsTouched.value = false
  touchedOptionGroupIds.value = new Set()
  productTouched.value = false
}
function stepQuantity(delta: number) {
  quantity.value = Math.max(1, Number(quantity.value || 1) + delta)
  quantityTouched.value = true
}
function onQuantityInput() {
  quantityTouched.value = true
}
function onGrayscaleChange() {
  grayscaleTouched.value = true
}

// Merkt sich die aktuell persistierte Größe (Master) – für Placeholder & Vergleich
const persistedSizeTitle = ref<string | null>(null)

/** ------------------------------------------------------------------------------------
 *  Product Query (Nuxt-safe)
 * ----------------------------------------------------------------------------------- */
const activeProductId = computed<number | null>(() => {
  if (isMulti.value && hasPendingConfigChanges.value) {
    return selectedProductId.value ?? null
  }
  return selectedProductId.value ?? props.productId ?? null
})
const product = computed(() => {
  const id = Number(activeProductId.value || 0)
  if (!(id > 0)) return null
  return catalogBootstrap.value?.products?.[String(id)] ?? null
})
const loading = computed(() => false)
const error = computed(() => null)
const shippingNote = computed(() => String((product.value as any)?.shippingInfo ?? ''))
const refetchProduct = async () => {}
const refetchProducts = async () => {}

function catImageUrl(cat: any) {
  const src = cat?.thumbnailWebp || cat?.thumbnail
  if (!src) return 'https://via.placeholder.com/320x200?text=Kategorie'
  return src.startsWith('http') ? src : `https://www.pixelfoto-express.de${src}`
}

/** Pending-Navigations-State: wird erst beim Apply auf die URL geschrieben */
const pendingSubcatId = ref<number | null>(null)
// Tracks the categoryId of the currently selected image
const effectiveCategoryId = ref<number | null>(null)
const treeItemsById = computed(() => {
  const map = new Map<number, any>()
  for (const item of catalogBootstrap.value?.tree?.items ?? []) {
    const id = Number((item as any)?._id ?? 0)
    if (id > 0) map.set(id, item)
  }
  return map
})
const treeChildrenByParent = computed(() => {
  const map = new Map<number, any[]>()
  for (const item of catalogBootstrap.value?.tree?.items ?? []) {
    const parentId = Number((item as any)?.parentId ?? 0)
    if (!(parentId > 0)) continue
    const list = map.get(parentId) ?? []
    list.push(item)
    map.set(parentId, list)
  }
  return map
})
const currentProductCategoryItem = computed<any | null>(() => {
  const categoryIds = Array.isArray((product.value as any)?.categoryIds)
    ? ((product.value as any)?.categoryIds as any[]).map((id) => Number(id || 0)).filter((id) => id > 0)
    : []
  if (!categoryIds.length) return null

  const preferredRootId = Number(props.category ?? 0) || null
  if (preferredRootId) {
    const matching = categoryIds.find((id) => {
      const item = treeItemsById.value.get(id)
      if (!item) return false
      return Number(item._id) === preferredRootId || Number(item.parentId ?? 0) === preferredRootId
    })
    if (matching) return treeItemsById.value.get(matching) ?? null
  }

  return treeItemsById.value.get(categoryIds[0]) ?? null
})
const currentRootCategoryId = computed<number | null>(() => {
  if (isMulti.value && hasPendingConfigChanges.value) {
    const pendingRootId = Number(props.category ?? 0) || null
    if (pendingRootId) return pendingRootId
  }
  const sidebarRootId = Number(props.parentCategoryId ?? props.category ?? 0) || null
  if (sidebarRootId) return sidebarRootId
  const effectiveItem = effectiveCategoryId.value ? (treeItemsById.value.get(Number(effectiveCategoryId.value)) ?? null) : null
  if (effectiveItem) {
    const parentId = Number(effectiveItem.parentId ?? 0)
    return parentId > 0 ? parentId : Number(effectiveItem._id ?? 0) || null
  }
  const item = currentProductCategoryItem.value
  if (item) {
    const parentId = Number(item.parentId ?? 0)
    return parentId > 0 ? parentId : Number(item._id ?? 0) || null
  }
  return null
})
const rawActiveSubcategoryId = computed<number | null>(() => {
  if (isMulti.value && hasPendingConfigChanges.value && !pendingSubcatId.value && !effectiveCategoryId.value) {
    return null
  }
  const candidate = pendingSubcatId.value
    ?? effectiveCategoryId.value
    ?? (props.activeSubcategoryId ? Number(props.activeSubcategoryId) : null)
    ?? (currentProductCategoryItem.value ? Number(currentProductCategoryItem.value._id ?? 0) : null)
    ?? null
  if (!(Number(candidate || 0) > 0)) return null
  const item = treeItemsById.value.get(Number(candidate))
  if (!item) return null
  return Number(item.parentId ?? 0) === Number(currentRootCategoryId.value ?? 0) ? Number(candidate) : null
})
const {
  subcategories: flowSubcategories,
  hasSubcategories: hasFlowSubcategories,
  selectedCategoryId: effectiveCategoryForQuery,
  products: productsInCategory,
  suggestedPrimaryStep,
} = useEditorCatalogFlow({
  tree: computed(() => catalogBootstrap.value?.tree ?? null),
  bootstrap: catalogBootstrap,
  selectedRootId: currentRootCategoryId,
  selectedSubId: rawActiveSubcategoryId,
  selectedProductId,
})
const effectiveFlowSubcategories = computed<any[]>(() =>
  flowSubcategories.value.length
    ? flowSubcategories.value
    : (!(catalogBootstrap.value?.tree?.items?.length) && Array.isArray(props.subcategories) ? props.subcategories : []),
)
const effectiveHasFlowSubcategories = computed(() => effectiveFlowSubcategories.value.length > 0)

function currentResolvedCategoryMeta() {
  const resolvedCategoryId = effectiveCategoryForQuery.value ?? effectiveCategoryId.value ?? null
  const resolvedCategoryItem = resolvedCategoryId ? (treeItemsById.value.get(Number(resolvedCategoryId)) ?? null) : null
  const resolvedParentId = resolvedCategoryItem?.parentId ? Number(resolvedCategoryItem.parentId) : null
  const resolvedCategoryTitle = String(
    effectiveFlowSubcategories.value.find((s) => Number(s._id) === Number(resolvedCategoryId || 0))?.title
    ?? resolvedCategoryItem?.title
    ?? '',
  ).trim() || null

  return {
    categoryId: resolvedCategoryId,
    categoryTitle: resolvedCategoryTitle,
    parentCategoryId: resolvedParentId,
  }
}

// Subkat-Auswahl im Accordion: nur lokal speichern, NICHT navigieren
function selectSubcategoryPending(sub: any) {
  hasPendingConfigChanges.value = true
  pendingSubcatId.value = Number(sub._id)
  selectedProductId.value = null
  selectedOptions.value = new Map()
  pendingSizePatch.value = null
  selectedFormat.value = ''
  selectedPrice.value = 0
  sizePreselectTitle.value = null
  closeGroup()
  closeAccordionAfterSelection('__subcats__')
  closeHoverPreview()
}

// Pending-State bei Selektion resetten (neue Bilder ausgewählt → alten Pending verwerfen)
const forceOpenPrimaryStep = ref(false)
watch(() => selectionIds.value.join('|'), () => {
  emit('options-preview', null)
  touchedOptionGroupIds.value = new Set()
  hasPendingConfigChanges.value = false
  pendingSubcatId.value = null
  forceOpenPrimaryStep.value = false
})

// Kategorie-Wechsel (z.B. User klickt andere Root-Kategorie): Pending-State und Produkt-Auswahl zurücksetzen
function resetSidebarForPendingRootChange() {
  closeHoverPreview()
  closeGroup()
  hasPendingConfigChanges.value = true
  forceOpenPrimaryStep.value = true
  suppressDisplayGroupFallback.value = true
  pendingSubcatId.value = null
  effectiveCategoryId.value = null
  selectedProductId.value = null
  selectedOptions.value = new Map()
  pendingSizePatch.value = null
  selectedFormat.value = ''
  selectedPrice.value = 0
  sizePreselectTitle.value = null
  openAccordionId.value = null
}

watch(() => props.category, (newCat, oldCat) => {
  if (newCat === oldCat) return
  if (isMulti.value && selectionIds.value.length > 0) {
    resetSidebarForPendingRootChange()
    return
  }
  suppressDisplayGroupFallback.value = false
  pendingSubcatId.value = null
  effectiveCategoryId.value = null
  selectedProductId.value = props.productId ?? null
  selectedOptions.value = new Map()
  pendingSizePatch.value = null
  selectedFormat.value = ''
  selectedPrice.value = 0
  sizePreselectTitle.value = null
})
watch([currentRootCategoryId, flowSubcategories], () => {
  if (flowSubcategories.value.length > 0) return
  pendingSubcatId.value = null
})
watch(productsInCategory, (list) => {
  if (Array.isArray(list) && list.length) suppressDisplayGroupFallback.value = false
  if (isMulti.value && hasPendingConfigChanges.value) return
  if (!catalogBootstrap.value) return
  const currentId = Number(selectedProductId.value || 0)
  if (!(currentId > 0)) return
  if (!Array.isArray(list) || list.length === 0) return
  const stillValid = (Array.isArray(list) ? list : []).some((p: any) => Number(p?._id) === currentId)
  if (stillValid) return
  selectedProductId.value = null
  pendingSizePatch.value = null
  selectedFormat.value = ''
})

/** Produktliste der aktuellen/pending Kategorie */
const productsEnabled = computed(() => !!effectiveCategoryForQuery.value)

/** ------------------------------------------------------------------------------------
 * Preselect Size: READ only
 * ----------------------------------------------------------------------------------- */
async function computeSizePreselectTitle(): Promise<string | null> {
  // Single: route imageId
  if (!isMulti.value) {
    const iid = String(route.query.imageId || '')
    if (!iid) return null
    const rec = await db.get(iid)
    return (rec as any)?.edits?.preset?.size?.title ?? null
  }

  // Multi: selection ids
  const ids = selectionIds.value
  if (!ids.length) return null

  const titles: string[] = []
  for (const id of ids) {
    const rec = await db.get(id)
    const t = (rec as any)?.edits?.preset?.size?.title
    if (t) titles.push(String(t))
  }

  if (!titles.length) return null

  const first = titles[0]
  const allSame = titles.every(t => t === first)
  return allSame ? first : first // Master = erstes
}

const currentProduct = computed(() => {
  const id = selectedProductId.value
  if (isMulti.value && hasPendingConfigChanges.value && !(Number(id || 0) > 0)) return null
  if (!(Number(id || 0) > 0)) return product.value
  return productsInCategory.value.find((p: any) => Number(p._id) === Number(id))
    ?? catalogBootstrap.value?.products?.[String(Number(id || 0))]
    ?? product.value
})

function shortTitle(input: any, max = 36): string {
  const raw = String(input ?? '').trim()
  if (!raw) return '—'
  return raw.length > max ? `${raw.slice(0, max - 1).trim()}…` : raw
}

// Klartext-Name (für Persistenz/Anzeige im Image-Tool)
const currentProductTitle = computed<string | null>(() => {
  const t = (currentProduct.value as any)?.title || (product.value as any)?.title
  return t ? String(t) : null
})
const currentProductShortTitle = computed<string>(() => shortTitle(currentProductTitle.value))
const lastSizeTitleBeforeProductSwitch = ref<string | null>(null)
// Merkt sich zusätzlich die Maße der zuletzt bewusst gewählten Größe, damit der
// Produktwechsel auch dann trifft, wenn die Titel der Size-Zeilen abweichen
// ("30 x 40 cm" vs. "30x40cm"). Wird nach einem Fallback NICHT überschrieben,
// damit A → B → A die ursprüngliche Wahl wiederherstellt.
const lastSizeBeforeProductSwitch = ref<{ title: string; widthMm: number; heightMm: number } | null>(null)
const lastSizeSwitchWasFallback = ref(false)

function rememberSizeChoiceForProductSwitch(title: string | null | undefined, widthMm?: number | null, heightMm?: number | null) {
  const t = String(title || '').trim()
  if (!t) return
  let w = Math.round(Number(widthMm || 0))
  let h = Math.round(Number(heightMm || 0))
  if (!(w > 0 && h > 0)) {
    const opt = realSizeOptionsForCurrentProduct().find((o: any) => String(o?.title || '').trim() === t)
    const dims = sizeDimsMm(opt || { title: t })
    w = dims?.widthMm ?? 0
    h = dims?.heightMm ?? 0
  }
  lastSizeTitleBeforeProductSwitch.value = t
  lastSizeBeforeProductSwitch.value = { title: t, widthMm: w, heightMm: h }
  lastSizeSwitchWasFallback.value = false
}

function realSizeOptionsForCurrentProduct() {
  const groups = Array.isArray(cfg.value?.groups) ? cfg.value.groups : []
  const group = groups.find((entry: any) => entry?.isSize)
  return Array.isArray(group?.sizeOptions) ? [...group.sizeOptions] : []
}

/** Design-Editor: aktuelles Preset der Sidebar (controlled) an den Eigentuemer melden. */
function controlledEmit(overrides: { productId?: number | null; size?: ImageSize | null } = {}) {
  const options: ImageOption[] = []
  for (const [groupId, sel] of selectedOptions.value.entries()) {
    options.push({ groupId: String(groupId), variantId: String(sel.variantId), value: String(sel.value) })
  }
  const sizeFromFormat = (() => {
    const title = selectedFormat.value
    const opt = title ? sizeOptions.value.find((o: any) => o.title === title) : null
    if (!opt) return null
    const dims = sizeDimsMm(opt)
    return { title, widthMm: dims?.widthMm, heightMm: dims?.heightMm, variantId: String(opt.variantId || opt.id || ''), ...sizeGeometryPayload(opt, dims?.widthMm, dims?.heightMm) } as ImageSize
  })()
  emit('preset-change', {
    productId: overrides.productId !== undefined ? overrides.productId : (activeProductId.value ?? selectedProductId.value ?? props.productId ?? null),
    size: overrides.size !== undefined ? overrides.size : (pendingSizePatch.value ?? sizeFromFormat),
    options,
    quantity: Math.max(1, Number(quantity.value || 1)),
    grayscale: !!grayscale.value,
  })
}

/** Inline Produkt-Auswahl: Umschalten zwischen Produkten der aktuellen Kategorie */
const ctxRef = ref<HTMLElement | null>(null)

async function switchProduct(nextId: number) {
  closeHoverPreview()
  hasPendingConfigChanges.value = true
  productTouched.value = true
  if (!lastSizeSwitchWasFallback.value) {
    // Nur eine bewusste Auswahl merken, nicht das Ergebnis eines vorherigen Fallbacks.
    const currentTitle = pendingSizePatch.value?.title || selectedFormat.value || persistedSizeTitle.value || null
    rememberSizeChoiceForProductSwitch(currentTitle, pendingSizePatch.value?.widthMm, pendingSizePatch.value?.heightMm)
  }
  selectedProductId.value = nextId
  closeGroup()
  selectedOptions.value = new Map()
  pendingSizePatch.value = null
  selectedFormat.value = ''
  selectedPrice.value = Number((catalogBootstrap.value?.products?.[String(nextId)] as any)?.basePrice ?? 0)
  // Kein router.push – Navigation erfolgt erst beim Apply
  await refetchProduct()
  await refetchProducts()
  await nextTick()
  applyDefaultOptionSelectionsForCurrentProduct()

  const targetSizeOptions = [...realSizeOptionsForCurrentProduct()].sort(compareSizeOptions)
  const previousSize = lastSizeBeforeProductSwitch.value
    || (lastSizeTitleBeforeProductSwitch.value ? { title: lastSizeTitleBeforeProductSwitch.value } : null)
  // Titel exakt → Titel normalisiert → gleiche Maße (auch gedreht). Erst dann Default.
  const matched = findMatchingSizeOption(targetSizeOptions as any[], previousSize)
  const fallback = matched || defaultSizeOption(targetSizeOptions as any[])
  lastSizeSwitchWasFallback.value = !matched
  const fallbackDims = sizeDimsMm(fallback as any)

  selectedFormat.value = fallback ? String(fallback.title || '') : ''
  if (fallback) {
    selectedPrice.value = Number((fallback as any).priceSpecial ?? (fallback as any).price ?? selectedPrice.value)
  }
  pendingSizePatch.value = fallback ? {
    title: String(fallback.title || ''),
    widthMm: fallbackDims?.widthMm ?? 0,
    heightMm: fallbackDims?.heightMm ?? 0,
    variantId: String((fallback as any).variantId ?? fallback?.id ?? ''),
    ...sizeGeometryPayload(fallback, fallbackDims?.widthMm, fallbackDims?.heightMm),
  } : null
  sizePreselectTitle.value = fallback ? String(fallback.title || '') : null

  if (props.controlled) {
    controlledEmit({ productId: nextId, size: pendingSizePatch.value })
    priceStale.value = true
    closeAccordionAfterSelection('__products__')
    return
  }

  const targetIds = isMulti.value ? selectionIds.value : (singleImageId.value ? [singleImageId.value] : [])
  if (!isMulti.value && targetIds.length) {
    // Build the NEW product's selected options and replace the old ones. Without
    // replaceOptions the previous product's option variants would be merged in and
    // survive the switch, producing cart items with variants from a foreign product
    // (wrong/0,00 SKU in production). See OrderItemSkuResolver foreign-variant guard.
    const nextOptions: ImageOption[] = []
    for (const g of cfg.value?.groups ?? []) {
      if (g?.isSize || !g?.id) continue
      const selectedId = String(g?.selected?.id || '')
      if (g?.selected && selectedId) {
        nextOptions.push({ groupId: String(g.id), variantId: selectedId, value: String(g.selected.title || '') })
      }
    }
    await patchPresetOnImages(targetIds, fallback ? {
      size: {
        title: String(fallback.title || ''),
        widthMm: fallbackDims?.widthMm ?? 0,
        heightMm: fallbackDims?.heightMm ?? 0,
        variantId: String((fallback as any).variantId ?? fallback?.id ?? ''),
        ...sizeGeometryPayload(fallback, fallbackDims?.widthMm, fallbackDims?.heightMm),
      },
      options: nextOptions,
    } : { size: null, options: nextOptions }, { resetCropToMax: true, replaceOptions: true })
  }

  priceStale.value = true
  closeAccordionAfterSelection('__products__')
}

function syncSidebarBaselineFromProps() {
  const propCategoryId = Number(props.activeSubcategoryId ?? props.category ?? 0) || null
  const propProductId = Number(props.productId ?? 0) || null
  effectiveCategoryId.value = propCategoryId
  pendingSubcatId.value = null
  selectedProductId.value = propProductId
}

function recordRootCategoryId(rec: any): number | null {
  const parentId = Number(rec?.parentCategoryId ?? rec?.edits?.preset?.parentCategoryId ?? 0) || null
  if (parentId) return parentId
  return Number(rec?.categoryId ?? rec?.edits?.preset?.categoryId ?? 0) || null
}

function shouldKeepSidebarTargetRoot(rec: any): boolean {
  if (!isMulti.value) return false
  const targetRootId = Number(props.category ?? 0) || null
  if (!targetRootId) return false
  const recRootId = recordRootCategoryId(rec)
  if (!recRootId) return false
  return Number(targetRootId) !== Number(recRootId)
}

async function syncConfiguratorProductFromSelection() {
  if (!isMulti.value) return
  const id = selectionIds.value[0]
  if (!id) {
    effectiveCategoryId.value = null
    pendingSubcatId.value = null
    selectedProductId.value = props.productId ?? null
    return
  }
  const rec = await db.get(id)
  resetMultiDirtyFlags()
  if (!rec) {
    hasPendingConfigChanges.value = false
    syncSidebarBaselineFromProps()
    return
  }
  if (shouldKeepSidebarTargetRoot(rec)) {
    syncSidebarBaselineFromProps()
    selectedOptions.value = new Map()
    pendingSizePatch.value = null
    selectedFormat.value = ''
    selectedPrice.value = 0
    sizePreselectTitle.value = null
    quantity.value = 1
    grayscale.value = false
    return
  }
  const liveCategoryId = Number(props.activeSubcategoryId ?? props.category ?? 0) || null
  const pid = Number((rec as any)?.productId ?? (rec as any)?.edits?.preset?.productId ?? 0)
  const cid = Number((rec as any)?.categoryId ?? (rec as any)?.edits?.preset?.categoryId ?? liveCategoryId ?? 0) || null
  effectiveCategoryId.value = cid
  pendingSubcatId.value = null
  if (pid !== Number(selectedProductId.value || 0)) {
    selectedProductId.value = pid || null
    await refetchProduct()
  }
  const preset = (rec as any)?.edits?.preset || {}
  applyPresetSelectionsForCurrentProduct(preset)
  quantity.value = Math.max(1, Number(preset?.quantity ?? 1))
  grayscale.value = !!preset?.grayscale
  selectedFormat.value = String(preset?.size?.title || '')
  pendingSizePatch.value = null
  sizePreselectTitle.value = String(preset?.size?.title || '') || null
  hasPendingConfigChanges.value = false
  lastSizeSwitchWasFallback.value = false
  resetMultiDirtyFlags()
}

watch(
    () => [isMulti.value, selectionIds.value.join('|')],
    async () => {
      if (hasPendingConfigChanges.value) return
      await syncConfiguratorProductFromSelection()
    },
    { immediate: true },
)

watch(
  () => [props.category, props.activeSubcategoryId, props.parentCategoryId, props.productId, selectionIds.value.join('|')],
  () => {
    if (!isMulti.value) return
    if (hasPendingConfigChanges.value) return
    syncSidebarBaselineFromProps()
  },
  { immediate: true },
)

onMounted(() => {
  void fetchEditorCatalogBootstrap({
    editorBaseUrl: getWidgetConfig().editorBaseUrl,
  }).then((payload) => {
    catalogBootstrap.value = payload
  }).catch(() => {})
})

/** ------------------------------------------------------------------------------------
 *  Config-Gruppen
 * ----------------------------------------------------------------------------------- */
const cfg = computed(() => ((product.value as any)?.configurator ?? null))

const displayGroups = computed(() => {
  const base = cfg.value?.groups ?? []
  if (!showUpload.value) return base
  const upload = [{ id: '__upload__', title: 'Foto auswählen', icon: '', isSize: false, description: '' }]
  return [...upload, ...base]
})

// Keep last non-empty groups to avoid blank overview during product-switch loading
const lastNonEmptyDisplayGroups = ref<any[]>([])
const suppressDisplayGroupFallback = ref(false)
watch(displayGroups, (g) => { if (g.length) lastNonEmptyDisplayGroups.value = [...g] }, { immediate: true })
// When no product is selected: always empty — prevents stale groups from previous category showing
const effectiveDisplayGroups = computed(() => {
  if (suppressDisplayGroupFallback.value) return displayGroups.value
  if (!selectedProductId.value) return []
  return displayGroups.value.length ? displayGroups.value : lastNonEmptyDisplayGroups.value
})

const sizeGroup = computed(() => effectiveDisplayGroups.value.find((g: any) => g.isSize))
const nonSizeDisplayGroups = computed(() =>
  effectiveDisplayGroups.value.filter((g: any) => !g.isSize && g.id !== '__upload__')
)
const bootstrapResolvedProduct = computed<any | null>(() => {
  const candidateIds = [
    Number(activeProductId.value || 0),
    Number(selectedProductId.value || 0),
    Number(props.productId || 0),
  ].filter((id) => id > 0)

  for (const id of candidateIds) {
    const hit = catalogBootstrap.value?.products?.[String(id)]
    if (hit) return hit
  }

  return null
})
const allowCustomSize = computed(() =>
  cfg.value?.product?.hasIndividualSizes === true
  || (currentProduct.value as any)?.hasIndividualSizes === true
  || (product.value as any)?.hasIndividualSizes === true
  || (bootstrapResolvedProduct.value as any)?.hasIndividualSizes === true
)

const sizeOptions = computed(() => Array.isArray(sizeGroup.value?.sizeOptions) ? [...sizeGroup.value.sizeOptions] : [])

function applyDefaultOptionSelectionsForCurrentProduct() {
  const next = new Map<string, { variantId: string; value: string }>()
  for (const group of (cfg.value?.groups ?? []) as any[]) {
    if (!group || group.isSize) continue
    const fallback = (group.options ?? []).find((o: any) => o?.isDefault) ?? (group.options ?? [])[0] ?? null
    if (!fallback || !group.id) continue
    next.set(String(group.id), {
      variantId: String(fallback.id ?? ''),
      value: String(fallback.title ?? ''),
    })
  }
  selectedOptions.value = next
}

function applyPresetSelectionsForCurrentProduct(preset: any) {
  const next = new Map<string, { variantId: string; value: string }>()
  const ownOptions = Array.isArray(preset?.options) ? preset.options : []
  for (const group of (cfg.value?.groups ?? []) as any[]) {
    if (!group || group.isSize || !group.id) continue
    const wanted = ownOptions.find((o: any) => String(o?.groupId ?? '') === String(group.id))
    const matched = wanted
      ? (Array.isArray(group.options) ? group.options : []).find((o: any) => String(o?.id ?? '') === String(wanted?.variantId ?? ''))
      : null
    const fallback = matched
      ?? (group.options ?? []).find((o: any) => o?.isDefault)
      ?? (group.options ?? [])[0]
      ?? null
    if (!fallback) {
      group.selected = null
      continue
    }
    group.selected = fallback
    next.set(String(group.id), {
      variantId: String(fallback.id ?? ''),
      value: String(fallback.title ?? ''),
    })
  }
  selectedOptions.value = next
}


const sizeGroupApiId = computed(() => {
  // nimm die echte VariantGroup.id aus dem Produkt, wo isSize=true
  const edges = product.value?.variantGroups?.edges ?? []
  const vg = edges.map((e: any) => e.node).find((n: any) => n?.isSize)
  return String(vg?.id || '')
})

const sizeIdToVariantId = computed(() => {
  const m = new Map<string, string>()
  const edges = product.value?.variants?.edges ?? []
  const sizeGroupId = sizeGroupApiId.value

  for (const e of edges) {
    const v = e?.node
    if (!v?.size?.id) continue
    if (sizeGroupId && String(v?.variantGroup?.id) !== sizeGroupId) continue // nur Größen
    m.set(String(v.size.id), String(v.id)) // ✅ sizeId -> variantId
  }
  return m
})

function variantIdFromSizeId(sizeId?: string | null) {
  if (!sizeId) return undefined
  return sizeIdToVariantId.value.get(String(sizeId))
}

// Lädt die aktuell persistierte Größe (Master) aus der DB und setzt sie als Placeholder/Selected.
async function refreshPersistedSize() {
  const { title, variantId } = await computePersistedSize()
  persistedSizeTitle.value = title
  persistedSizeVariantId.value = variantId
  sizePreselectTitle.value = title

  if (!pendingSizePatch.value && title) {
    selectedFormat.value = title
    const m = sizeOptions.value.find((o: any) => o.title === title)
    if (m) selectedPrice.value = Number(m.priceSpecial ?? m.price ?? selectedPrice.value)
  }
}

async function hydrateSidebarFromDb(preferredImageId?: string) {
  hydratingFromDb.value = true
  try {
    if (isMulti.value) {
      syncSidebarBaselineFromProps()
      resetMultiDirtyFlags()
    }
    const activeId = activeImageIdForSidebar(preferredImageId)
    if (!activeId) {
      await refreshPersistedSize()
      quantity.value = 1
      grayscale.value = false
      imageMeta.value = null
      selectedImage.value = 'Kein Bild'
      return
    }

    const rec: any = await db.get(activeId)
    if (isMulti.value && useLargeMultiSelectionFastPath.value) {
      const preset = rec?.edits?.preset || {}
      const liveCategoryId = Number(props.activeSubcategoryId ?? props.category ?? 0) || null
      effectiveCategoryId.value = Number(rec?.categoryId ?? preset?.categoryId ?? liveCategoryId ?? 0) || null
      pendingSubcatId.value = null
      selectedProductId.value = Number(rec?.productId ?? preset?.productId ?? props.productId ?? 0) || null
      await refetchProduct()
      applyPresetSelectionsForCurrentProduct(preset)
      await refreshPersistedSize()
      quantity.value = Math.max(1, Number(preset?.quantity ?? 1))
      grayscale.value = !!preset?.grayscale
      if (preset?.size?.title) {
        selectedFormat.value = String(preset.size.title || '')
        sizePreselectTitle.value = String(preset.size.title || '')
      }
      imageMeta.value = null
      selectedImage.value = `${selectionIds.value.length} Bilder`
      return
    }
    if (shouldKeepSidebarTargetRoot(rec)) {
      syncSidebarBaselineFromProps()
      selectedOptions.value = new Map()
      pendingSizePatch.value = null
      selectedFormat.value = ''
      selectedPrice.value = 0
      sizePreselectTitle.value = null
      quantity.value = 1
      grayscale.value = false
      await loadImageMetaFromDB(activeId)
      await refreshSelectedImageLabel(activeId)
      return
    }
    const preset = rec?.edits?.preset || {}
    const liveCategoryId = Number(props.activeSubcategoryId ?? props.category ?? 0) || null
    effectiveCategoryId.value = Number(rec?.categoryId ?? preset?.categoryId ?? liveCategoryId ?? 0) || null
    pendingSubcatId.value = null
    selectedProductId.value = Number(rec?.productId ?? preset?.productId ?? props.productId ?? 0) || null
    await refetchProduct()
    applyPresetSelectionsForCurrentProduct(preset)
    await refreshPersistedSize()
    quantity.value = Math.max(1, Number(preset?.quantity ?? 1))
    grayscale.value = !!preset?.grayscale
    if (preset?.size?.title) {
      selectedFormat.value = String(preset.size.title || '')
      sizePreselectTitle.value = String(preset.size.title || '')
    }
    await loadImageMetaFromDB(activeId)
    await refreshSelectedImageLabel(activeId)
  } finally {
    hydratingFromDb.value = false
  }
}

function onExternalEditorImagesChanged(e: Event) {
  const detail = (e as CustomEvent<any>)?.detail || {}
  const ids = Array.isArray(detail?.ids) ? detail.ids.filter(Boolean).map((x: any) => String(x)) : []
  const action = String(detail?.action || 'updated')
  const patch = detail?.patch && typeof detail.patch === 'object' ? detail.patch : null
  const activeId = activeImageIdForSidebar()

  if (action === 'removed') {
    if (!activeId || ids.includes(activeId)) {
      quantity.value = 1
      grayscale.value = false
      imageMeta.value = null
      selectedImage.value = 'Kein Bild'
    }
    void hydrateSidebarFromDb()
    return
  }

  if (patch && patch.quantity != null && activeId && ids.includes(activeId)) {
    quantity.value = Math.max(1, Number(patch.quantity || 1))
  }
  if (patch && patch.grayscale != null && activeId && ids.includes(activeId)) {
    grayscale.value = !!patch.grayscale
    return
  }

  if (!activeId || !ids.length || ids.includes(activeId)) {
    void hydrateSidebarFromDb(activeId)
  }
}

/** Defaults für Size */
watch(
    sizeGroup,
    (g: any) => {
      if (!g) return
      // Fallback nur, wenn noch keine DB-Preselect verfügbar ist
      if (!persistedSizeTitle.value && !pendingSizePatch.value) {
        selectedFormat.value = g.selected?.title || ''
        selectedPrice.value = g.selected?.price ?? (cfg.value?.product?.basePrice ?? 0)
      }
    },
    { immediate: true },
)

// ✅ Wenn Sidebar geöffnet/Selection wechselt: DB-Size als Placeholder/Selected laden
watch(
    () => [isMulti.value, props.productId, selectionIds.value.join('|')],
    async () => {
      if (hasPendingConfigChanges.value) return
      pendingSizePatch.value = null
      await hydrateSidebarFromDb()
    },
    { immediate: true },
)

function groupValueLabel(group: any) {
  if (group.id === '__upload__') return selectedImage.value
  if (group.isSize) return selectedFormat.value || (loading.value ? 'lädt…' : '—')
  return group.selected?.title || '—'
}
function groupDescription(group: any) {
  return group.description || ''
}

/** Gruppen öffnen/schließen */
function groupComponentFor(group: any): 'size' | 'upload' | 'placeholder' | null {
  if (group.id === '__upload__') return showUpload.value ? 'upload' : null
  if (group.isSize) return 'size'
  return 'placeholder'
}

async function openGroupFor(grp: any) {
  const key = groupComponentFor(grp)
  if (!key) return

  if (key === 'size') {
    // Beim Öffnen der Size-Gruppe: aktuellen DB-Wert als Start/Placeholder setzen
    pendingSizePatch.value = null
    await refreshPersistedSize()
  }

  activeGroupId.value = grp.id
  activeComponentKey.value = key
  emit('changeGroup', activeComponentKey.value)
  activeGroupTitle.value = grp.title
}
function closeGroup() {
  emit('changeGroup', null)
  activeGroupId.value = null
  activeComponentKey.value = null
}

/** ------------------------------------------------------------------------------------
 *  ✅ Persistenz: NUR beim APPLY (Multi)
 *  Single bleibt wie gehabt: sofort persistieren
 * ----------------------------------------------------------------------------------- */

function normRot(deg: number) {
  const r = Number(deg) || 0
  return ((r % 360) + 360) % 360
}

function maxCropForRecord(rec: any, size: ImageSize | null | undefined) {
  return buildMaxCropForRecord(rec as any, size as any)
}

async function patchPresetOnImages(
  ids: string[],
  patch: { size?: ImageSize | null; options?: ImageOption[]; quantity?: number; grayscale?: boolean },
  opts?: { resetCropToMax?: boolean; syncCart?: boolean; replaceOptions?: boolean; keepProductIdentity?: boolean },
) {
  const touched: string[] = []
  // Only a real size/options/crop change needs the heavy editor reload
  // (server re-fetch + tile re-hydrate). A pure S/W (grayscale) or quantity
  // toggle is presentational and must NOT trigger it — otherwise applying to
  // many selected images re-fetches + re-crops every tile and can drop
  // thumbnails. Multi-apply always re-sends `options`, so presence alone is not
  // enough; we compare the actual option content per image.
  let optionsGeometryChanged = false
  // Neue Optionen je Bild: der Editor übernimmt sie sofort per Event in seine
  // Kacheln. Sonst bleibt dort (z. B. bei übersprungenem Reload nach einer
  // Größen-Übernahme) die alte Auswahl stehen und landet so im Warenkorb.
  const optionsById: Record<string, ImageOption[]> = {}
  const metaBatchItems: Array<{ id: string; productId?: number | null; categoryId?: number | null; meta: Record<string, any> | null }> = []
  const editorBaseUrl = String((getWidgetConfig() as any)?.editorBaseUrl || '').trim()
  const projectCode = String(project.projectCode.value || await project.ensureProject().catch(() => '') || '').trim()
  const remoteItems = (!editorBaseUrl || !projectCode)
    ? []
    : await fetchProjectUploads({
        editorBaseUrl,
        projectCode,
        limit: 500,
        offset: 0,
      }).catch(() => [])
  const remoteById = new Map(
    remoteItems
      .map((item) => mapRemoteUploadToRecordMeta(item))
      .map((rec) => [String((rec as any)?.id || '').trim(), rec]),
  )

  for (const id of ids) {
    const rec = (await db.get(id)) || remoteById.get(String(id || '').trim()) || transientUploads.get(String(id || '').trim()) || null
    if (!rec) continue

    const prevEdits: any = rec.edits ?? {}
    const prevPreset: any = prevEdits.preset ?? {}

    let nextOptions: ImageOption[] = Array.isArray(prevPreset.options)
        ? (prevPreset.options as any[]).map((o: any) => ({
          groupId: String(o?.groupId ?? o?.id ?? ''),
          variantId: String(o?.variantId ?? ''),
          value: String(o?.value ?? ''),
        })).filter(o => o.groupId)
        : []

    if (patch.options) {
      const map = new Map<string, ImageOption>()
      if (!opts?.replaceOptions) {
        nextOptions.forEach(o => map.set(String(o.groupId), o))
      }
      patch.options.forEach(o => map.set(String(o.groupId), {
        groupId: String(o.groupId),
        variantId: String(o.variantId),
        value: String(o.value),
      }))
      nextOptions = [...map.values()]
    }

    const cropAllowedForOptions = (options: ImageOption[]) => {
      for (const rawOpt of options) {
        const groupId = normId(rawOpt?.groupId ?? rawOpt?.id ?? '')
        const variantId = normId(rawOpt?.variantId ?? '')
        if (!groupId || !variantId) continue
        const group = (cfg.value?.groups ?? []).find((g: any) => normId(g?.id) === groupId)
        if (!group || group?.isSize) continue
        const selected = (Array.isArray(group?.options) ? group.options : []).find((o: any) => normId(o?.id) === variantId)
        if (selected?.isCrop === false) return false
      }
      return true
    }
    const prevCropAllowed = cropAllowedForOptions(
      Array.isArray(prevPreset.options)
        ? (prevPreset.options as any[]).map((o: any) => ({
            groupId: String(o?.groupId ?? o?.id ?? ''),
            variantId: String(o?.variantId ?? ''),
            value: String(o?.value ?? ''),
          })).filter((o) => o.groupId)
        : [],
    )
    if (patch.options) optionsById[String(id)] = nextOptions
    const nextCropAllowed = cropAllowedForOptions(nextOptions)
    const cropModeChanged = prevCropAllowed !== nextCropAllowed

    if (patch.options) {
      const optSig = (list: any[]) => JSON.stringify(
        (Array.isArray(list) ? list : [])
          .map((o: any) => [String(o?.groupId ?? o?.id ?? ''), String(o?.variantId ?? '')])
          .filter((p) => p[0])
          .sort(),
      )
      if (cropModeChanged || optSig(prevPreset.options) !== optSig(nextOptions)) {
        optionsGeometryChanged = true
      }
    }

    const sidebarProductId = activeProductId.value ?? props.productId ?? null
    const sidebarCategoryId = effectiveCategoryForQuery.value ?? effectiveCategoryId.value ?? null
    const prevProductId = Number((rec as any)?.productId ?? prevPreset?.productId ?? 0) || null
    const prevCategoryId = Number((rec as any)?.categoryId ?? prevPreset?.categoryId ?? 0) || null
    const keepIdentity = !!opts?.keepProductIdentity && !!prevProductId
    const resolvedProductId = keepIdentity ? prevProductId : sidebarProductId
    const resolvedCategoryId = keepIdentity ? (prevCategoryId ?? sidebarCategoryId) : sidebarCategoryId
    // Platzhalter wie "Produkt #0" (Bild vor der Produktwahl hochgeladen) nie uebernehmen,
    // sonst bleibt er bis in die Bestellung stehen.
    const prevTitle = String(prevPreset?.productTitle ?? (rec as any)?.productTitle ?? '').trim()
    const prevTitleUsable = prevTitle !== '' && !/^(produkt|product|artikel)\s*#?\s*\d*$/i.test(prevTitle)
    const resolvedProductTitle = keepIdentity
      ? (prevTitleUsable ? prevTitle : currentProductTitle.value)
      : currentProductTitle.value
    const resolvedCategoryItem = resolvedCategoryId ? (treeItemsById.value.get(Number(resolvedCategoryId)) ?? null) : null
    const resolvedParentId = resolvedCategoryItem?.parentId ? Number(resolvedCategoryItem.parentId) : null
    // Kategorie-Titel: aus aktuellem Flow/Katalog auflösen (für image-tool Gruppenheader)
    const resolvedCategoryTitleCandidate =
      flowSubcategories.value.find(s => Number(s._id) === resolvedCategoryId)?.title
      ?? resolvedCategoryItem?.title
      ?? String(prevPreset?.categoryTitle ?? (rec as any)?.categoryTitle ?? '').trim()
    const resolvedCategoryTitle = String(resolvedCategoryTitleCandidate || '').trim() || null

    const nextPreset: any = {
      ...prevPreset,
      ...(patch.size !== undefined ? { size: patch.size } : {}),
      // Bei Beschnitt deckt der Crop das volle Format ab; Fulfillment liest dieses Flag.
      ...(patch.size !== undefined ? { bleedIncluded: Number((patch.size as any)?.bleedMm || 0) > 0 } : {}),
      ...(patch.quantity !== undefined ? { quantity: patch.quantity } : {}),
      ...(patch.grayscale !== undefined ? { grayscale: !!patch.grayscale } : {}),
      ...(patch.options ? { options: nextOptions } : {}),
      productId: resolvedProductId,
      productTitle: resolvedProductTitle,
      categoryId: resolvedCategoryId,
      categoryTitle: resolvedCategoryTitle,
      parentCategoryId: resolvedParentId,
    }

    const prevSizeId = normId(prevPreset?.size?.id ?? '')
    const nextSizeId = normId(nextPreset?.size?.id ?? '')
    const prevSizeTitle = String(prevPreset?.size?.title ?? '').trim()
    const nextSizeTitle = String(nextPreset?.size?.title ?? '').trim()
    const prevWidthMm = Math.round(Number(prevPreset?.size?.widthMm ?? 0) || 0)
    const prevHeightMm = Math.round(Number(prevPreset?.size?.heightMm ?? 0) || 0)
    const nextWidthMm = Math.round(Number(nextPreset?.size?.widthMm ?? 0) || 0)
    const nextHeightMm = Math.round(Number(nextPreset?.size?.heightMm ?? 0) || 0)
    const sameResolvedSize =
      (prevSizeId && nextSizeId ? prevSizeId === nextSizeId : true) &&
      prevSizeTitle === nextSizeTitle &&
      prevWidthMm === nextWidthMm &&
      prevHeightMm === nextHeightMm

    const nextEdits: any = { ...prevEdits, preset: nextPreset }
    const nextRecordForCrop = {
      ...(rec as any),
      edits: nextEdits,
    } as any

    if (patch.size !== undefined) {
      nextEdits.lockAR = true
      nextEdits.orientMode = 'auto'
      nextEdits.swapAR = false
      nextEdits.crop = maxCropForRecord(nextRecordForCrop, patch.size ?? nextPreset?.size ?? null)
    } else if (cropModeChanged && nextCropAllowed) {
      nextEdits.lockAR = true
      nextEdits.orientMode = 'auto'
      nextEdits.swapAR = false
      nextEdits.crop = maxCropForRecord(nextRecordForCrop, nextPreset?.size ?? null)
    } else if (opts?.resetCropToMax) {
      nextEdits.crop = maxCropForRecord(nextRecordForCrop, patch.size ?? nextPreset?.size ?? null)
    }

    const nextRecord = {
      ...(rec as any),
      edits: nextEdits,
      productId: resolvedProductId,
      productTitle: resolvedProductTitle,
      categoryId: resolvedCategoryId,
      categoryTitle: resolvedCategoryTitle,
      parentCategoryId: resolvedParentId,
      updatedAt: Date.now(),
    } as any

    if (await db.get(id)) {
      await db.update(id, {
        edits: nextEdits,
        productId: resolvedProductId,
        productTitle: resolvedProductTitle,
        categoryId: resolvedCategoryId,
        categoryTitle: resolvedCategoryTitle,
        parentCategoryId: resolvedParentId,
        updatedAt: Date.now(),
      } as any)
    }

    // Frisch hochgeladene Bilder liegen zusätzlich im Transient-Store; dessen
    // Preset gewinnt beim Editor-Reload (mergePersistedRecordWithTransientPreview).
    // Ohne Patch springen Optionen nach „Auswahl übernehmen“ auf den Upload-Stand zurück.
    transientUploads.patch(String(id), {
      edits: nextEdits,
      productId: resolvedProductId,
      productTitle: resolvedProductTitle,
      categoryId: resolvedCategoryId,
      categoryTitle: resolvedCategoryTitle,
      parentCategoryId: resolvedParentId,
    } as any)

    if (editorBaseUrl && projectCode) {
      metaBatchItems.push({
        id,
        productId: Number(resolvedProductId || 0) || null,
        categoryId: Number(resolvedCategoryId || 0) || null,
        meta: { edits: nextEdits },
      })
    }

    if (opts?.syncCart) {
      cart.syncImageRecord(nextRecord as any).catch(() => {})
    }

    Object.assign(rec as any, {
      edits: nextEdits,
      productId: resolvedProductId,
      productTitle: resolvedProductTitle,
      categoryId: resolvedCategoryId,
      categoryTitle: resolvedCategoryTitle,
      parentCategoryId: resolvedParentId,
      updatedAt: Date.now(),
    })

    touched.push(id)
  }

  if (editorBaseUrl && projectCode && metaBatchItems.length) {
    await syncUploadMetaBatch({
      editorBaseUrl,
      projectCode,
      items: metaBatchItems,
    }).catch(() => [])
    // Die Server-Liste wurde oben (vor dem Schreiben) geholt und 5 s gecacht.
    // Ohne Leeren lädt der Editor-Reload gleich danach die alten Optionen zurück.
    invalidateProjectUploadsCache({ editorBaseUrl, projectCode })
  }

  if (touched.length) {
    const requiresEditorReload =
      patch.size !== undefined ||
      optionsGeometryChanged ||
      !!opts?.resetCropToMax

    window.dispatchEvent(new CustomEvent(EDITOR_IMAGES_CHANGED_EVENT, {
      detail: {
        ids: touched,
        action: 'updated',
        patch: {
          ...(patch.quantity !== undefined ? { quantity: patch.quantity } : {}),
          ...(patch.grayscale !== undefined ? { grayscale: !!patch.grayscale } : {}),
          ...(patch.options ? { optionsById } : {}),
        },
      },
    }))
    if (requiresEditorReload) {
      emit('db-updated', touched)
    }
  }
}

/** Size-Änderung */
async function forwardSizeChange(
    w: number,
    h: number,
    displayTitle?: string,
    sizeOptionId?: string, // <-- das ist bei dir die Size-ID
) {
  if (displayTitle) {
    selectedFormat.value = displayTitle
  }

  const sel =
      (sizeOptionId ? sizeOptions.value.find((o: any) => String(o.id) === String(sizeOptionId)) : null) ||
      (displayTitle ? sizeOptions.value.find((o: any) => o.title === displayTitle) : null)
  const resolvedVariantId = String(
      (sel as any)?.variantId ||
      variantIdFromSizeId(sizeOptionId) ||
      sizeOptionId ||
      '',
  )

  if (sel?.price != null) selectedPrice.value = Number(sel.price) || selectedPrice.value

  if (props.controlled) {
    const payload: ImageSize = {
      title: displayTitle,
      widthMm: Math.round(w * 10),
      heightMm: Math.round(h * 10),
      variantId: resolvedVariantId || undefined,
      ...sizeGeometryPayload(sel, Math.round(w * 10), Math.round(h * 10)),
    }
    pendingSizePatch.value = payload
    controlledEmit({ size: payload })
    emit('changeSize', w, h)
    nextTick(() => closeGroup())
    return
  }

  if (isMulti.value) {
    const ids = selectionIds.value
    if (ids.length && displayTitle) {
      hasPendingConfigChanges.value = true
      pendingSizePatch.value = {
        title: displayTitle,
        widthMm: Math.round(w * 10),
        heightMm: Math.round(h * 10),
        variantId: resolvedVariantId || undefined,
        ...sizeGeometryPayload(sel, Math.round(w * 10), Math.round(h * 10)),
      }
    }
  } else {
    const iid = String(route.query.imageId || '')
    if (iid) {
      const payload: ImageSize = {
        title: displayTitle,
        widthMm: Math.round(w * 10),
        heightMm: Math.round(h * 10),
        variantId: resolvedVariantId || undefined, // ✅ "/api/variants/...."
        ...sizeGeometryPayload(sel, Math.round(w * 10), Math.round(h * 10)),
      }
      await patchPresetOnImages([iid], { size: payload }, { syncCart: true })
    }
  }

  emit('changeSize', w, h)
  if (isMulti.value) nextTick(() => closeGroup())
}

function previewCustomSizeChange(
  w: number,
  h: number,
  displayTitle?: string,
) {
  if (!displayTitle) return
  rememberSizeChoiceForProductSwitch(displayTitle, Math.round(w * 10), Math.round(h * 10))
  selectedFormat.value = displayTitle
  selectedPrice.value = 0
  if (props.controlled) {
    const payload: ImageSize = { title: displayTitle, widthMm: Math.round(w * 10), heightMm: Math.round(h * 10), variantId: undefined, ...sizeGeometryPayload(null, Math.round(w * 10), Math.round(h * 10)) }
    pendingSizePatch.value = payload
    controlledEmit({ size: payload })
    return
  }
  if (isMulti.value && selectionIds.value.length) {
    hasPendingConfigChanges.value = true
    pendingSizePatch.value = {
      title: displayTitle,
      widthMm: Math.round(w * 10),
      heightMm: Math.round(h * 10),
      variantId: undefined,
      ...sizeGeometryPayload(null, Math.round(w * 10), Math.round(h * 10)),
    }
  }
}

function onSingleSizeClicked() {
  if (isMulti.value) return
  nextTick(() => closeGroup())
}

/** Variant/Option auswählen */
async function onSelectVariant(optionId: string) {
  const groupId = activeGroupId.value
  if (!groupId) return

  const grp: any = cfg.value?.groups?.find((g: any) => g.id === groupId)
  const next = grp?.options?.find((o: any) => o.id === optionId)
  if (next) grp.selected = next

  const opt: ImageOption = {
    groupId: String(groupId),
    variantId: String(next?.id || optionId),     // ✅ DAS ist die Variant-ID (z.B. 65810)
    value: String(next?.title || ''),
  }

  if (props.controlled) {
    selectedOptions.value.set(String(groupId), { variantId: opt.variantId, value: opt.value })
    controlledEmit()
    return
  }

  if (!isMulti.value) {
    const iid = String(route.query.imageId || '')
    if (iid) {
      await patchPresetOnImages([iid], { options: [opt] }, { syncCart: true })
      await loadImageMetaFromDB(iid)
    }
  }
  // Multi: Änderungen werden beim Apply übernommen (kein Live-Preview)
  if (isMulti.value) {
    hasPendingConfigChanges.value = true
    optionsTouched.value = true
    touchedOptionGroupIds.value = new Set([...touchedOptionGroupIds.value, String(groupId)])
    emitOptionsPreview()
  }
}

/**
 * Beobachte die Menge-Auswahl.
 *
 * Multi-Mode: nur Preview-Änderung senden (keine DB).
 * Single-Mode: sofort persistieren über patchPresetOnImages.
 */
watch(
    quantity,
    async (newVal, oldVal) => {
      if (hydratingFromDb.value) return
      const qty = Math.max(1, Number(newVal || 1))
      if (props.controlled) {
        controlledEmit()
        return
      }
      if (!isMulti.value) {
        // Single-Mode: sofort persistieren
        const iid = String(route.query.imageId || '')
        if (iid) await patchPresetOnImages([iid], { quantity: qty }, { syncCart: true })
      }
      // Multi: Menge wird beim Apply geschrieben
      if (isMulti.value) hasPendingConfigChanges.value = true
    },
    { immediate: false },
)

watch(
  grayscale,
  async (newVal) => {
    if (hydratingFromDb.value) return
    if (props.controlled) {
      controlledEmit()
      return
    }
    if (!isMulti.value) {
      const iid = String(route.query.imageId || '')
      if (iid) await patchPresetOnImages([iid], { grayscale: !!newVal }, { syncCart: true })
    }
    if (isMulti.value) hasPendingConfigChanges.value = true
  },
  { immediate: false },
)

/** Apply (Multi): EIN DB-WRITE pro Apply */
const persistedSizeVariantId = ref<string | null>(null)

async function computePersistedSize(): Promise<{ title: string | null; variantId: string | null }> {
  // Single
  if (!isMulti.value) {
    const iid = String(route.query.imageId || '')
    if (!iid) return { title: null, variantId: null }
    const rec = await db.get(iid)
    const size = (rec as any)?.edits?.preset?.size ?? null
    return {
      title: size?.title ?? null,
      variantId: size?.variantId ?? null,
    }
  }

  // Multi: schneller Master = erstes selektiertes Bild
  const firstId = String(selectionIds.value[0] || '').trim()
  if (firstId) {
    const rec = await db.get(firstId)
    const size = (rec as any)?.edits?.preset?.size ?? null
    if (size?.title) {
      return {
        title: size?.title ?? null,
        variantId: size?.variantId ?? null,
      }
    }
  }

  return { title: null, variantId: null }
}


async function onApplyConfig() {
  if (!isMulti.value) return
  const ids = selectionIds.value
  if (!ids.length) return
  if (isApplyingConfig.value) return
  if (!hasPendingConfigChanges.value && !pendingSizePatch.value) {
    closeGroup()
    emit("applied")
    return
  }

  isApplyingConfig.value = true

  try {
    const persisted = {
      title: persistedSizeTitle.value,
      variantId: persistedSizeVariantId.value,
    }

    let sizePatch: ImageSize | null | undefined = undefined

    if (pendingSizePatch.value?.title) {
      const pending = pendingSizePatch.value

      const titleChanged = (persisted.title ?? null) !== (pending.title ?? null)
      const variantChanged = (persisted.variantId ?? null) !== (pending.variantId ?? null)

      if (persisted.title == null || titleChanged || variantChanged) {
        sizePatch = { ...pending }
      }
    }

    const nextOptions: ImageOption[] = []
    for (const g of cfg.value?.groups ?? []) {
      if (g?.isSize) continue
      if (!g?.id) continue
      const selectedId = String(g?.selected?.id || "")
      if (g?.selected && selectedId) {
        nextOptions.push({
          groupId: String(g.id),
          variantId: selectedId,
          value: String(g.selected.title || ""),
        })
      }
    }

    if (sizePatch) {
      emit('apply-size-local', { ids: [...ids], size: { ...sizePatch } })
    }
    // Optionen gehören zum Produkt: nach einem Produktwechsel müssen sie
    // ersetzt werden, sonst nur, wenn der Nutzer sie geändert hat.
    const writeOptions = optionsTouched.value || productTouched.value
    // Ohne Produktwechsel nur geänderte Gruppen, in die Optionen jedes Bildes gemischt.
    const replaceAllOptions = productTouched.value
    const optionsToWrite = replaceAllOptions
      ? nextOptions
      : nextOptions.filter((o) => touchedOptionGroupIds.value.has(String(o.groupId)))
    // Produkt/Kategorie nur umschreiben, wenn etwas Produktspezifisches
    // (Produktwechsel, Größe, Optionen) angewendet wird. Eine reine Mengen-
    // oder S/W-Änderung darf Bilder nicht in ein anderes Produkt verschieben.
    const keepProductIdentity = !productTouched.value && sizePatch === undefined && !writeOptions
    await patchPresetOnImages(ids, {
      ...(sizePatch !== undefined ? { size: sizePatch } : {}),
      ...(quantityTouched.value ? { quantity: Math.max(1, Number(quantity.value || 1)) } : {}),
      ...(grayscaleTouched.value ? { grayscale: !!grayscale.value } : {}),
      ...(writeOptions && optionsToWrite.length ? { options: optionsToWrite } : {}),
    }, {
      syncCart: false,
      replaceOptions: replaceAllOptions,
      keepProductIdentity,
    })

    pendingSizePatch.value = null
    hasPendingConfigChanges.value = false
    resetMultiDirtyFlags()
    if (sizePatch?.title) {
      persistedSizeTitle.value = sizePatch.title ?? null
      persistedSizeVariantId.value = sizePatch.variantId ?? null
    }

    const navCatId = effectiveCategoryForQuery.value ?? props.category ?? null
    const navProdId = Number(activeProductId.value ?? props.productId ?? 0) || null
    emit("navigate", { categoryId: navCatId, productId: navProdId })
    pendingSubcatId.value = null

    closeGroup()
    emit("applied")
  } finally {
    window.setTimeout(() => {
      isApplyingConfig.value = false
    }, 240)
  }
}

async function onSaveSingle() {
  const iid = singleImageId.value
  if (!iid) return
  try {
    const qty = Math.max(1, Number(quantity.value || 1))
    const size = resolveCurrentSizePatch()
    const options = collectSelectedOptions()
    await patchPresetOnImages([iid], { size, options, quantity: qty, grayscale: !!grayscale.value }, { syncCart: true, replaceOptions: true })
    toast.success('Konfiguration gespeichert')
    emit('applied')
  } catch (e: any) {
    toast.error(e?.message || 'Konnte nicht speichern.')
  }
}

function toVariantNumber(v: any): number {
  const s = String(v ?? '').trim()
  if (!s) return 0
  const m = s.match(/(\d+)\s*$/)
  if (m) return Number(m[1]) || 0
  const n = Number(s)
  return Number.isFinite(n) ? n : 0
}

function normalizedCartCrop(rec: any, preset: any) {
  const crop = rec?.edits?.crop
  if (crop && Number(crop.w) > 0 && Number(crop.h) > 0) {
    return {
      x: Math.max(0, Math.round(Number(crop.x) || 0)),
      y: Math.max(0, Math.round(Number(crop.y) || 0)),
      width: Math.max(1, Math.round(Number(crop.w) || 0)),
      height: Math.max(1, Math.round(Number(crop.h) || 0)),
    }
  }
  return preset?.crop ?? null
}

function normalizedCartPreset(rec: any, preset: any, qty: number) {
  return {
    ...preset,
    quantity: qty,
    crop: normalizedCartCrop(rec, preset),
    rotate: Number(rec?.edits?.rotate ?? preset?.rotate ?? preset?.crop?.rot ?? 0) || 0,
  }
}

function collectSelectedOptions(): ImageOption[] {
  const nextOptions: ImageOption[] = []
  for (const g of cfg.value?.groups ?? []) {
    if (g?.isSize) continue
    if (!g?.id || !g?.selected) continue
    // Auch die Standard-/erste Variante ausdrücklich speichern: mit replaceOptions fehlte sie sonst
    // ganz (Retroprint „Glanz“ → Auftrag ohne Oberfläche, S3.26.316421).
    const selectedId = String(g.selected.id || '')
    if (!selectedId) continue
    nextOptions.push({
      groupId: String(g.id),
      variantId: selectedId,
      value: String(g.selected.title || ''),
    })
  }
  return nextOptions
}

function resolveCurrentSizePatch(): ImageSize | null {
  const title = String(selectedFormat.value || '').trim()
  if (!title) return null
  const sel = sizeOptions.value.find((o: any) => String(o?.title || '') === title)
  if (!sel) {
    const parsed = parseSizeTitle(title)
    if (parsed && allowCustomSize.value) {
      return {
        title: parsed.title,
        widthMm: parsed.wMm,
        heightMm: parsed.hMm,
      }
    }
  }
  return {
    title,
    widthMm: sel?.widthMm != null ? Number(sel.widthMm) : undefined,
    heightMm: sel?.heightMm != null ? Number(sel.heightMm) : undefined,
    variantId: sel ? String((sel as any)?.variantId || (sel as any)?.id || '') : undefined,
  }
}

async function onAddToCartSingle() {
  try {
    const iid = singleImageId.value
    if (!iid) {
      showSingleCartUploadInfo()
      return
    }
    const qty = Math.max(1, Number(quantity.value || 1))
    const size = resolveCurrentSizePatch()
    const options = collectSelectedOptions()

    await patchPresetOnImages([iid], {
      size,
      options,
      quantity: qty,
      grayscale: !!grayscale.value,
    }, { syncCart: false, replaceOptions: true })

    const rec: any = await db.get(iid)
    if (!rec || !isUploadDone(rec)) {
      singleUploadReady.value = false
      showSingleCartUploadInfo()
      return
    }
    await cart.syncImageRecord(rec as any)
    toast.success('Konfiguration gespeichert und in den Warenkorb gelegt')
  } catch (e: any) {
    toast.error(e?.message || 'Konnte nicht in den Warenkorb legen.')
  }
}

/** Upload (nur Single/klassisch) */
function onImagesUploaded(ids: string[]) {
  const imageId = String((ids || [])[0] || '')
  if (!imageId) return
  void refreshSelectedImageLabel(imageId)
  closeGroup()
  router.push({ query: { ...route.query, imageId } })
}

async function refreshSelectedImageLabel(preferredId?: string) {
  const idFromArg = String(preferredId || '').trim()
  const idFromRoute = String(route.query.imageId || '').trim()
  const idFromSelection = String(selectionIds.value[0] || '').trim()
  const id = idFromArg || (isMulti.value ? idFromSelection : idFromRoute)

  if (!id) {
    selectedImage.value = 'Kein Bild'
    return
  }

  const rec: any = await db.get(id)
  const name = String(rec?.name || rec?.fileName || rec?.originalName || '').trim()
  selectedImage.value = name || `Bild ${id.slice(0, 8)}`
}

/** Bild-Meta (nur Single) */
const imageMeta = ref<{ widthPx: number; heightPx: number; aspectRatio: number } | null>(null)

function activeImageIdForSidebar(preferredId?: string): string {
  const idFromArg = String(preferredId || '').trim()
  if (idFromArg) return idFromArg
  if (isMulti.value) return String(selectionIds.value[0] || '').trim()
  return String(route.query.imageId || '').trim()
}

async function loadImageMetaFromDB(imageId: string) {
  if (!imageId) {
    imageMeta.value = null
    return
  }
  const rec = await db.get(imageId)
  const blob = rec?.workingBlob || rec?.originalBlob
  if (!blob) { imageMeta.value = null; return }

  const url = URL.createObjectURL(blob)
  try {
    const img = new Image()
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve()
      img.onerror = reject
      img.src = url
    })
    const presetOptions = Array.isArray((rec as any)?.edits?.preset?.options)
      ? (rec as any).edits.preset.options
      : []

    const cropAllowed = !presetOptions.some((rawOpt: any) => {
      const groupId = normId(rawOpt?.groupId ?? rawOpt?.id ?? '')
      const variantId = normId(rawOpt?.variantId ?? '')
      if (!groupId || !variantId) return false

      const group = (cfg.value?.groups ?? []).find((g: any) => normId(g?.id) === groupId)
      if (!group || group?.isSize) return false

      const selected = (Array.isArray(group?.options) ? group.options : []).find((o: any) => normId(o?.id) === variantId)
      return selected?.isCrop === false
    })

    const widthPx = cropAllowed
      ? Math.max(1, Math.round(Number((rec as any)?.edits?.crop?.w) || img.naturalWidth))
      : Math.max(1, img.naturalWidth)
    const heightPx = cropAllowed
      ? Math.max(1, Math.round(Number((rec as any)?.edits?.crop?.h) || img.naturalHeight))
      : Math.max(1, img.naturalHeight)

    imageMeta.value = {
      widthPx,
      heightPx,
      aspectRatio: widthPx / heightPx,
    }
  } finally {
    URL.revokeObjectURL(url)
  }
}

onMounted(async () => {
  window.addEventListener(EDITOR_IMAGES_CHANGED_EVENT, onExternalEditorImagesChanged as EventListener)
  offUploadBus?.()
  offUploadBus = onUploadEvent((event) => {
    const eventId = String((event as any)?.id || '').trim()
    const activeSingleId = singleImageId.value

    if (event?.type === 'upload:done') {
      if (!isMulti.value && activeSingleId && eventId === activeSingleId) singleUploadReady.value = true
    }

    if (event?.type === 'upload:patched' && uploadPatchLooksReady((event as any)?.patch)) {
      if (!isMulti.value && activeSingleId && eventId === activeSingleId) singleUploadReady.value = true
    }

    refreshCartAvailability().catch(() => {})
  })
  await hydrateSidebarFromDb()
  await refreshCartAvailability()
})
onBeforeUnmount(() => {
  window.removeEventListener(EDITOR_IMAGES_CHANGED_EVENT, onExternalEditorImagesChanged as EventListener)
  offUploadBus?.()
  offUploadBus = null
})
watch(
    () => route.query.imageId,
    async (newId: any) => {
      if (isMulti.value) return
      const activeId = activeImageIdForSidebar(String(newId || ''))
      await hydrateSidebarFromDb(activeId)
      await refreshCartAvailability()
    },
)
watch(
    () => selectionIds.value.join('|'),
    async () => {
      if (!isMulti.value) return
      if (hasPendingConfigChanges.value) return
      await hydrateSidebarFromDb()
      await refreshCartAvailability()
    },
    { immediate: true },
)

/** Helpers */
function catThumb(cat: any): string {
  const src = cat?.thumbnailWebp || cat?.thumbnail
  if (!src) return 'https://via.placeholder.com/44x44?text=K'
  return src.startsWith('http') ? src : `https://www.pixelfoto-express.de${src}`
}

function getImageUrl(obj?: { thumbnail?: string; thumbnailWebp?: string; icon?: string } | null) {
  const ph = 'https://via.placeholder.com/80x80?text=Kategorie'
  if (!obj) return ph
  const src = obj.thumbnailWebp || obj.thumbnail || obj.icon
  if (!src) return ph
  return src.startsWith('http') ? src : `https://www.pixelfoto-express.de${src}`
}

/** Preis-Kalkulator */
const selectedSize = computed(() => {
  const byTitle = sizeOptions.value.find((o: any) => o.title === selectedFormat.value)
  if (byTitle) return byTitle
  const parsed = parseSizeTitle(selectedFormat.value)
  if (parsed && allowCustomSize.value) {
    return {
      id: '__custom__',
      title: parsed.title,
      widthMm: parsed.wMm,
      heightMm: parsed.hMm,
      price: 0,
      priceSpecial: 0,
    }
  }
  return byTitle || null
})

const { price, currency, loading: priceLoading, diffsByGroup, sizeDiffs, groupTotals, fmtCurrency } = usePriceCalculator({
  cfg: () => cfg.value,
  quantity: () => quantity.value,
  sizeOption: () => selectedSize.value,
  categoryId: () => props.category ?? null,
  currentGroupId: () => activeGroupId.value,
})

function formatPriceFallback(value: number): string {
  return `${Number(value || 0).toFixed(2).replace('.', ',')} €`
}

// Preisunterschied je Stück neben nicht gewählten Varianten ("+2,50 €"), aus usePriceCalculator.
function optDelta(grp: any, opt: any): number | undefined {
  if (!grp || !opt || opt.id === grp.selected?.id) return undefined
  const d = diffsByGroup.value?.[String(grp.id)]?.[String(opt.id)]
  return typeof d === 'number' ? d : undefined
}
function optDeltaText(grp: any, opt: any): string {
  const d = optDelta(grp, opt)
  if (d === undefined) return ''
  const abs = fmtCurrency ? fmtCurrency(Math.abs(d)) : formatPriceFallback(Math.abs(d))
  return `${d > 0 ? '+' : d < 0 ? '−' : '±'}${abs}`
}
function optDeltaClass(grp: any, opt: any): string {
  const d = optDelta(grp, opt)
  return d === undefined ? '' : d > 0 ? 'up' : d < 0 ? 'down' : 'flat'
}
// Aufpreis der gewählten Option im Gruppenkopf ("4 cm · +11,55 €"), wie der Stückpreis bei der Größe.
function groupSurchargeLabel(grp: any): string {
  if (!grp || grp.isSize || grp.id === '__upload__' || !grp.selected) return ''
  const t = groupTotals.value?.[String(grp.id)]
  if (typeof t !== 'number' || Math.abs(t) < 0.005) return ''
  const abs = fmtCurrency ? fmtCurrency(Math.abs(t)) : formatPriceFallback(Math.abs(t))
  return `${t > 0 ? '+' : '−'}${abs}`
}

const totalPriceLabel = computed(() => {
  if (priceLoading.value || price.value <= 0) return ''
  return fmtCurrency ? fmtCurrency(price.value) : formatPriceFallback(price.value)
})
watch(totalPriceLabel, (label) => { emit('price-change', label || '') }, { immediate: true })

// ── Accordion ─────────────────────────────────────────────
const openAccordionId = ref<string | null>(null)
function toggleAccordion(id: string) {
  openAccordionId.value = openAccordionId.value === id ? null : id
}
function closeAccordionAfterSelection(id?: string | null) {
  const wanted = String(id || '').trim()
  if (!wanted || openAccordionId.value === wanted) openAccordionId.value = null
}

// Inline-Format-Liste: alle Größen sind immer sichtbar (keine 6er-Vorschau,
// kein innerer Scrollbereich), damit der Kunde das komplette Angebot sieht.
const visibleSizeOptions = computed(() => [...sizeOptions.value].sort(compareSizeOptions))
const inlineCustomEnabled = ref(false)
const inlineCustomWidth = ref<number | null>(null)
const inlineCustomHeight = ref<number | null>(null)

function roundInlineSize(value: any): number {
  const num = Number(value || 0)
  if (!Number.isFinite(num) || num <= 0) return 0
  return Math.round(num * 10) / 10
}

function syncInlineCustomFromSelected() {
  const title = String(selectedFormat.value || '').trim()
  if (!title) return
  const matched = sizeOptions.value.find((o: any) => String(o?.title || '') === title)
  const parsed = parseSizeTitle(title)
  if (!parsed) {
    if (matched) inlineCustomEnabled.value = false
    return
  }
  inlineCustomWidth.value = roundInlineSize(parsed.wCm)
  inlineCustomHeight.value = roundInlineSize(parsed.hCm)
  inlineCustomEnabled.value = allowCustomSize.value && !matched
}

watch(selectedFormat, () => {
  syncInlineCustomFromSelected()
}, { immediate: true })

/** Beim Einschalten mit der aktuell gewählten Größe vorbelegen – sonst stehen alte Werte
 *  (z. B. 20×30) in den Feldern und werden sofort als Vorschau übernommen. */
function toggleInlineCustom() {
  if (inlineCustomEnabled.value) {
    inlineCustomEnabled.value = false
    return
  }
  const current: any = selectedSize.value
  const w = Number(current?.widthMm ?? 0) / 10
  const h = Number(current?.heightMm ?? 0) / 10
  const parsed = (w > 0 && h > 0) ? null : parseSizeTitle(String(current?.title || selectedFormat.value || ''))
  const nextW = roundInlineSize(w > 0 ? w : parsed?.wCm)
  const nextH = roundInlineSize(h > 0 ? h : parsed?.hCm)
  if (nextW > 0 && nextH > 0) {
    inlineCustomWidth.value = nextW
    inlineCustomHeight.value = nextH
  }
  inlineCustomEnabled.value = true
}

watch(allowCustomSize, (allowed) => {
  if (!allowed) inlineCustomEnabled.value = false
}, { immediate: true })

const inlineCustomErrors = computed(() => {
  const out: string[] = []
  const w = roundInlineSize(inlineCustomWidth.value)
  const h = roundInlineSize(inlineCustomHeight.value)
  const minW = Number((cfg.value?.product as any)?.minSizeX ?? 0) || 0
  const minH = Number((cfg.value?.product as any)?.minSizeY ?? 0) || 0
  const maxW = Number((cfg.value?.product as any)?.maxSizeX ?? 0) || 0
  const maxH = Number((cfg.value?.product as any)?.maxSizeY ?? 0) || 0

  if (w <= 0 || h <= 0) {
    out.push('Bitte Breite und Höhe eingeben.')
    return out
  }
  if (minW > 0 && w < minW) out.push(`Breite mindestens ${minW} cm.`)
  if (maxW > 0 && w > maxW) out.push(`Breite maximal ${maxW} cm.`)
  if (minH > 0 && h < minH) out.push(`Höhe mindestens ${minH} cm.`)
  if (maxH > 0 && h > maxH) out.push(`Höhe maximal ${maxH} cm.`)
  return out
})
const inlineCustomValid = computed(() => inlineCustomErrors.value.length === 0)
const inlineCustomLimitHint = computed(() => {
  const minW = Number((cfg.value?.product as any)?.minSizeX ?? 0) || 0
  const minH = Number((cfg.value?.product as any)?.minSizeY ?? 0) || 0
  const maxW = Number((cfg.value?.product as any)?.maxSizeX ?? 0) || 0
  const maxH = Number((cfg.value?.product as any)?.maxSizeY ?? 0) || 0
  const parts: string[] = []
  if (minW > 0 || maxW > 0) parts.push(`Breite ${minW > 0 ? `ab ${minW}` : ''}${minW > 0 && maxW > 0 ? ' ' : ''}${maxW > 0 ? `bis ${maxW}` : ''} cm`)
  if (minH > 0 || maxH > 0) parts.push(`Höhe ${minH > 0 ? `ab ${minH}` : ''}${minH > 0 && maxH > 0 ? ' ' : ''}${maxH > 0 ? `bis ${maxH}` : ''} cm`)
  return parts.join(' · ')
})

watch([inlineCustomEnabled, inlineCustomWidth, inlineCustomHeight], ([enabled]) => {
  if (!enabled) return
  const w = roundInlineSize(inlineCustomWidth.value)
  const h = roundInlineSize(inlineCustomHeight.value)
  if (!inlineCustomValid.value || w <= 0 || h <= 0) return
  previewCustomSizeChange(w, h, `${w} x ${h} cm`)
})

/** Größe direkt aus dem Accordion wählen */
async function selectSizeInline(opt: any) {
  const title = String(opt?.title || '')
  let w = Number((opt as any)?.widthMm ?? 0) / 10
  let h = Number((opt as any)?.heightMm ?? 0) / 10
  if (!w || !h) {
    const dims = sizeDimsMm(opt)
    if (dims) { w = dims.widthMm / 10; h = dims.heightMm / 10 }
  }
  rememberSizeChoiceForProductSwitch(title, Math.round(w * 10), Math.round(h * 10))
  await forwardSizeChange(w, h, title, String((opt as any)?.id || ''))
  closeAccordionAfterSelection(sizeGroup.value?.id || null)
}

/** Variant aus Accordion wählen (non-size groups) */
async function selectVariantInline(groupId: string, variantId: string) {
  // Merke activeGroupId für onSelectVariant
  activeGroupId.value = groupId
  await onSelectVariant(variantId)
  activeGroupId.value = null
  closeAccordionAfterSelection(groupId)
}

const selectedSizeDims = computed(() => {
  const title = selectedFormat.value
  if (!title) return null
  const m = title.replace(/cm/ig, '').match(/(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)/i)
  if (!m) return null
  return `${m[1]} × ${m[2]} cm`
})

const activeSubcatId = computed<number | null>(() =>
  pendingSubcatId.value
  ?? rawActiveSubcategoryId.value
)
const hasSelectionFlow = computed(() => isMulti.value && selectionIds.value.length > 0)
const flowKey = computed(() => {
  const rootId = Number(currentRootCategoryId.value || 0)
  const childKey = flowSubcategories.value.map((s) => String((s as any)?._id ?? '')).join(',')
  return `${rootId}:${childKey}`
})
const showSubcategoryStep = computed(() => !!effectiveHasFlowSubcategories.value)
const showProductsStep = computed(() => {
  if (!productsInCategory.value.length) return false
  if (!effectiveHasFlowSubcategories.value) return true
  if (activeSubcatId.value) return true
  return !hasSelectionFlow.value
})
const showSizeStep = computed(() => {
  if (!sizeGroup.value) return false
  if (!hasSelectionFlow.value) return true
  return !!selectedProductId.value
})

const lastAutoFlowKey = ref('')
watch(
  [hasSelectionFlow, flowKey, activeSubcatId, selectedProductId, sizeGroup, showProductsStep, effectiveHasFlowSubcategories],
  () => {
    if (!hasSelectionFlow.value) {
      lastAutoFlowKey.value = ''
      forceOpenPrimaryStep.value = false
      return
    }
    if (flowKey.value && flowKey.value !== lastAutoFlowKey.value) {
      lastAutoFlowKey.value = flowKey.value
      openAccordionId.value = suggestedPrimaryStep.value === 'sub'
        ? '__subcats__'
        : (forceOpenPrimaryStep.value ? '__products__' : null)
      forceOpenPrimaryStep.value = false
      return
    }
    if (forceOpenPrimaryStep.value) {
      openAccordionId.value = effectiveHasFlowSubcategories.value
        ? '__subcats__'
        : (showProductsStep.value ? '__products__' : null)
      if (openAccordionId.value) forceOpenPrimaryStep.value = false
      return
    }
    if (effectiveHasFlowSubcategories.value && !activeSubcatId.value) {
      openAccordionId.value = '__subcats__'
      return
    }
    if (showProductsStep.value && !selectedProductId.value) {
      openAccordionId.value = '__products__'
      return
    }
    if (showSizeStep.value && sizeGroup.value?.id) {
      openAccordionId.value = sizeGroup.value.id
      return
    }
    openAccordionId.value = null
  },
  { immediate: true },
)

const activeSubcatTitle = computed(() => {
  const id = activeSubcatId.value
  if (!id) return null
  return effectiveFlowSubcategories.value.find(s => Number(s._id) === id)?.title ?? null
})
const activeSubcatThumb = computed(() => {
  const id = activeSubcatId.value
  if (!id) return ''
  const sub = effectiveFlowSubcategories.value.find(s => Number(s._id) === id)
  return sub ? catThumb(sub) : ''
})

const selectedProductTitle = computed(() => {
  const p: any = currentProduct.value
  return p?.title ? shortTitle(p.title, 28) : null
})
const selectedProductThumb = computed(() => {
  const p: any = currentProduct.value
  return p ? getImageUrl(p) : ''
})

function money(value: number): string {
  return fmtCurrency ? fmtCurrency(value) : formatPriceFallback(value)
}

/** Normalpreis einer Option, wenn ein Aktionspreis greift (sonst null). */
function regularPriceOf(opt: any): number | null {
  const regular = Number(opt?.regularPrice ?? 0)
  const now = Number(opt?.priceSpecial ?? opt?.price ?? 0)
  return regular > 0 && regular > now ? regular : null
}

/**
 * Preis eines Produkts der Kategorie im aktuell gewaehlten Format – macht sichtbar, dass z. B.
 * Matt 60x90 im Angebot ist, Glanz 60x90 aber nicht (Oberflaeche wechselt das Produkt).
 */
function productFormatPrice(p: any): { price: number; regular: number | null } | null {
  if (!selectedFormat.value) return null
  const prod: any = catalogBootstrap.value?.products?.[String(p?._id)] ?? p
  const groups: any[] = prod?.configurator?.groups ?? prod?.groups ?? []
  const opt = groups.find((g: any) => g?.isSize)?.sizeOptions?.find((o: any) => o?.title === selectedFormat.value)
  if (!opt || !(Number(opt.price) > 0)) return null
  return { price: Number(opt.price), regular: regularPriceOf(opt) }
}

function acSizeValue() {
  if (!selectedFormat.value) return null
  const sel = sizeOptions.value.find((o: any) => o.title === selectedFormat.value)
  const parsed = parseSizeTitle(selectedFormat.value)
  const shortTitle = parsed ? `${parsed.wCm}×${parsed.hCm} cm` : selectedFormat.value
  const p = sel ? (sel.priceSpecial ?? sel.price ?? 0) : 0
  if (!(p > 0)) return shortTitle
  const pStr = fmtCurrency ? fmtCurrency(p) : formatPriceFallback(p)
  return `${shortTitle} · ${pStr}`
}

/** Normalpreis des gewaehlten Formats (durchgestrichen im Gruppenkopf), sonst null. */
function acSizeRegular(): number | null {
  if (!selectedFormat.value) return null
  return regularPriceOf(sizeOptions.value.find((o: any) => o.title === selectedFormat.value))
}

// Zeigt an ob Preis durch Tile-Änderung veraltet ist (Warenkorb aktualisieren)
const priceStale = ref(false)
const isApplyingConfig = ref(false)
const applyingSelectionCount = computed(() => Math.max(0, Number(selectionIds.value.length || 0)))

async function refreshAndMarkStale() {
  await refreshPersistedSize()
  priceStale.value = true
}

function clearStale() {
  priceStale.value = false
}

async function onAddToCart() {
  // Design-Editor: Warenkorb-Logik liegt beim Eigentuemer (Proof + Design-Dokument)
  if (props.controlled) {
    emit('add-to-cart')
    return
  }
  if (isMulti.value) {
    try {
      await onApplyConfig()
      emit('add-to-cart')
    } catch (e: any) {
      toast.error(e?.message || 'Konnte nicht speichern.')
    }
  } else {
    await onAddToCartSingle()
  }
  clearStale()
}

defineExpose({ refreshPersistedSize: refreshAndMarkStale })
</script>

<template>
  <div class="sidebar-wrapper">
    <div v-if="props.logoUrl" class="sidebar-logo">
      <a
        class="sidebar-logo__link"
        :href="overviewLinkHref"
        aria-label="Zur Startseite"
        @click.prevent="requestLeaveEditor(overviewLinkHref)"
      >
        <img class="sidebar-logo__img" :src="props.logoUrl" alt="Pixelfoto Express" loading="lazy" decoding="async" />
      </a>
    </div>

    <!-- Übersicht -->
    <div
        class="view overview-view"
        v-show="!activeGroupId"
    >
      <!-- Multi: Kein Bild gewählt → Erklärungsscreen -->
      <div v-if="isMulti && selectionIds.length === 0" class="no-sel">
        <div class="no-sel__icon" aria-hidden="true">
          <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="4" y="8" width="40" height="32" rx="4" stroke="currentColor" stroke-width="2.2"/>
            <circle cx="16" cy="20" r="4" stroke="currentColor" stroke-width="2"/>
            <path d="M4 34l10-9 7 7 7-8 10 10" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <h3 class="no-sel__title">Fotos konfigurieren &amp; bestellen</h3>
        <p class="no-sel__desc">Wählen Sie rechts ein oder mehrere Bilder aus der Galerie aus oder laden Sie neue Fotos hoch, um Produkt, Format und Optionen festzulegen.</p>
        <ol class="no-sel__steps">
          <li>
            <span class="no-sel__step-num">1</span>
            <span class="no-sel__step-text"><strong>Foto auswählen oder hochladen</strong> – tippe auf ein Bild in der Galerie rechts oder lade neue Bilder in die Galerie hoch.</span>
          </li>
          <li>
            <span class="no-sel__step-num">2</span>
            <span class="no-sel__step-text"><strong>Produkt &amp; Format wählen</strong> – Oberfläche, Größe und weitere Optionen erscheinen hier.</span>
          </li>
          <li>
            <span class="no-sel__step-num">3</span>
            <span class="no-sel__step-text"><strong>Auswahl übernehmen</strong> – die Konfiguration wird gespeichert, in der Galerie aktualisiert und kann in den Warenkorb gelegt werden.</span>
          </li>
        </ol>
      </div>

        <!-- ── Accordion ────────────────────────────────────── -->
        <template v-if="!isMulti || selectionIds.length > 0">

        <div v-if="isMulti && selectionIds.length > 0" class="multi-hint">
          <div class="multi-hint__top">
            <span class="multi-hint__title">Bearbeitung</span>
            <span class="multi-hint__pill">{{ selectionIds.length }} ausgewählt</span>
          </div>
          <div class="multi-hint__sub">Kategorie, Produkt und Optionen wählen, dann unten auf <strong>Auswahl übernehmen</strong> klicken.</div>
        </div>

        <!-- Kategorie (Unterkategorien) -->
        <div v-if="showSubcategoryStep" class="ac-item ac-item--primary">
          <button class="ac-head" type="button" @click="toggleAccordion('__subcats__')">
            <img v-if="activeSubcatThumb" class="ac-head__icon ac-head__icon--primary" :src="activeSubcatThumb" :alt="activeSubcatTitle || ''" />
            <span class="ac-head__left">
              <span class="ac-head__title">Kategorie</span>
              <span v-if="activeSubcatTitle" class="ac-head__val">{{ activeSubcatTitle }}</span>
            </span>
            <svg class="ac-head__chev" :class="{ open: openAccordionId === '__subcats__' }" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
          </button>
          <div v-if="openAccordionId === '__subcats__'" class="ac-body">
            <div class="prod-grid">
              <button
                v-for="sub in effectiveFlowSubcategories"
                :key="sub._id"
                class="prod-card"
                :class="{ active: Number(sub._id) === activeSubcatId }"
                type="button"
                @click="selectSubcategoryPending(sub)"
                @mouseenter="openHoverPreview($event, sub, catThumb)"
                @mouseleave="closeHoverPreview()"
              >
                <div class="prod-card__img-wrap">
                  <img class="prod-card__img" :src="catThumb(sub)" :alt="sub.title" loading="lazy" decoding="async" />
                  <svg v-if="Number(sub._id) === activeSubcatId" class="prod-card__check" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                </div>
                <span class="prod-card__name">{{ sub.title }}</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Produkte der Kategorie – nur wenn keine Subkats vorhanden ODER eine Subkat gewählt/pending -->
        <div v-if="showProductsStep" class="ac-item ac-item--primary" ref="ctxRef">
          <button class="ac-head" type="button" @click="toggleAccordion('__products__')">
            <img v-if="selectedProductThumb" class="ac-head__icon ac-head__icon--primary" :src="selectedProductThumb" :alt="selectedProductTitle || ''" />
            <span class="ac-head__left">
              <span class="ac-head__title">Produkte</span>
              <span v-if="selectedProductTitle" class="ac-head__val">{{ selectedProductTitle }}</span>
            </span>
            <svg class="ac-head__chev" :class="{ open: openAccordionId === '__products__' }" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
          </button>
          <div v-if="openAccordionId === '__products__'" class="ac-body">
            <button
              v-for="p in productsInCategory"
              :key="p.id"
              class="ac-opt"
              :class="{ active: Number(p._id) === Number(selectedProductId) }"
              type="button"
              @click="switchProduct(Number(p._id))"
              @mouseenter="openHoverPreview($event, p, getImageUrl)"
              @mouseleave="closeHoverPreview()"
            >
              <img class="ac-opt__thumb" :src="getImageUrl(p)" :alt="p.title" loading="lazy" decoding="async" />
              <span class="ac-opt__name">{{ shortTitle(p.title, 30) }}</span>
              <span v-if="productFormatPrice(p)" class="ac-opt__price" :class="{ 'is-sale': productFormatPrice(p)?.regular !== null }">
                <span v-if="productFormatPrice(p)?.regular !== null" class="sale-badge">Aktion</span>
                <s v-if="productFormatPrice(p)?.regular !== null" class="fmt-row__regular">{{ money(productFormatPrice(p)?.regular ?? 0) }}</s>
                {{ money(productFormatPrice(p)?.price ?? 0) }}
              </span>
              <svg v-if="Number(p._id) === Number(selectedProductId)" class="ac-opt__check" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
            </button>
          </div>
        </div>

        <!-- Format (Größen) -->
        <div v-if="showSizeStep" class="ac-item">
          <button class="ac-head" type="button" @click="toggleAccordion(sizeGroup.id)">
            <img v-if="sizeGroup.icon" class="ac-head__icon" :src="sizeGroup.icon" alt="" />
            <span class="ac-head__left">
              <span class="ac-head__title">{{ sizeGroup.title || 'Format' }}</span>
              <span v-if="acSizeValue()" class="ac-head__val">{{ acSizeValue() }}<s v-if="acSizeRegular() !== null" class="ac-head__regular">{{ money(acSizeRegular() ?? 0) }}</s></span>
            </span>
            <svg class="ac-head__chev" :class="{ open: openAccordionId === sizeGroup.id }" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
          </button>
          <div v-if="openAccordionId === sizeGroup.id" class="ac-body ac-body--sizes">
            <button
              v-for="opt in visibleSizeOptions"
              :key="opt.id"
              class="fmt-row"
              :class="{ active: opt.title === selectedFormat }"
              type="button"
              @click="selectSizeInline(opt)"
            >
              <span class="fmt-row__name">
                {{ opt.title }}
                <span v-if="regularPriceOf(opt) !== null" class="sale-badge">Aktion</span>
              </span>
              <span class="fmt-row__price" :class="{ 'is-sale': regularPriceOf(opt) !== null }">
                <s v-if="regularPriceOf(opt) !== null" class="fmt-row__regular">{{ money(regularPriceOf(opt) ?? 0) }}</s>
                {{ money(opt.priceSpecial ?? opt.price ?? 0) }} / Stk.
              </span>
            </button>
            <div v-if="allowCustomSize" class="fmt-custom">
              <button
                class="fmt-custom__toggle"
                :class="{ active: inlineCustomEnabled }"
                type="button"
                @click="toggleInlineCustom"
              >
                <span class="fmt-custom__toggle-copy">
                  <span class="fmt-custom__toggle-title">Individuelle Maße</span>
                  <span class="fmt-custom__toggle-sub">Eigene Breite und Höhe eingeben</span>
                </span>
                <span class="fmt-custom__toggle-switch" :class="{ on: inlineCustomEnabled }" aria-hidden="true">
                  <span class="fmt-custom__toggle-knob" />
                </span>
              </button>

              <div v-if="inlineCustomEnabled" class="fmt-custom__panel">
                <div class="fmt-custom__sub">Breite ist waagerecht, Höhe senkrecht.</div>
                <div class="fmt-custom__fields">
                  <label class="fmt-custom__field">
                    <span>Breite</span>
                    <input v-model.number="inlineCustomWidth" type="number" min="0" step="0.1" inputmode="decimal" />
                  </label>
                  <label class="fmt-custom__field">
                    <span>Höhe</span>
                    <input v-model.number="inlineCustomHeight" type="number" min="0" step="0.1" inputmode="decimal" />
                  </label>
                </div>
                <div v-if="inlineCustomLimitHint" class="fmt-custom__hint">{{ inlineCustomLimitHint }}</div>
                <div v-if="inlineCustomErrors.length" class="fmt-custom__error">{{ inlineCustomErrors[0] }}</div>
                <div v-else class="fmt-custom__price">
                  <span class="fmt-custom__price-label">Preis</span>
                  <strong class="fmt-custom__price-value">
                    <template v-if="priceLoading">…</template>
                    <template v-else-if="price > 0">{{ fmtCurrency ? fmtCurrency(price) : formatPriceFallback(price) }}</template>
                    <template v-else>Wird berechnet</template>
                  </strong>
                </div>
                <div class="fmt-custom__apply-hint">Wird mit <strong>Auswahl übernehmen</strong> gespeichert.</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Andere Gruppen (Beschichtung, Rahmen, …) -->
        <div v-for="grp in nonSizeDisplayGroups" :key="grp.id" class="ac-item">
          <button class="ac-head" type="button" @click="grp.id === '__upload__' ? openGroupFor(grp) : toggleAccordion(grp.id)">
            <img v-if="grp.icon" class="ac-head__icon" :src="grp.icon" alt="" />
            <span class="ac-head__left">
              <span class="ac-head__title">{{ grp.title }}</span>
              <span v-if="groupValueLabel(grp) && groupValueLabel(grp) !== '—'" class="ac-head__val">{{ groupValueLabel(grp) }}<template v-if="groupSurchargeLabel(grp)"> · <span class="ac-head__surcharge">{{ groupSurchargeLabel(grp) }}</span></template></span>
            </span>
            <svg class="ac-head__chev" :class="{ open: openAccordionId === grp.id }" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
          </button>
          <div v-if="grp.id !== '__upload__' && openAccordionId === grp.id" class="ac-body">
            <button
              v-for="opt in (grp.options || [])"
              :key="opt.id"
              class="ac-opt"
              :class="{ active: opt.id === grp.selected?.id }"
              type="button"
              @click="selectVariantInline(grp.id, opt.id)"
            >
              <img v-if="opt.thumbnail" class="ac-opt__thumb" :src="opt.thumbnail" :alt="opt.title" loading="lazy" decoding="async" />
              <span class="ac-opt__name">{{ opt.title }}</span>
              <span v-if="optDeltaText(grp, opt)" class="ac-opt__delta" :class="optDeltaClass(grp, opt)">{{ optDeltaText(grp, opt) }}</span>
              <svg v-if="opt.id === grp.selected?.id" class="ac-opt__check" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
            </button>
          </div>
        </div>

        <div v-if="!isMulti || selectionIds.length > 0" class="ac-item">
          <button class="ac-head" type="button" @click="toggleAccordion('__settings__')">
            <span class="ac-head__left">
              <span class="ac-head__title">Bildstil &amp; Anzahl</span>
              <span class="ac-head__val">
                {{ grayscale ? 'S/W' : 'Farbe' }} · {{ quantity }}× pro Bild
              </span>
            </span>
            <svg class="ac-head__chev" :class="{ open: openAccordionId === '__settings__' }" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
          </button>
          <div v-if="openAccordionId === '__settings__'" class="ac-body ac-body--settings">
            <div class="settings-card">
              <label class="settings-field">
                <span class="settings-field__label">Anzahl pro Bild</span>
                <div class="settings-qty">
                  <button class="settings-qty__step" type="button" @click="stepQuantity(-1)">−</button>
                  <input v-model.number="quantity" class="settings-qty__input" type="number" min="1" step="1" inputmode="numeric" @input="onQuantityInput" />
                  <button class="settings-qty__step" type="button" @click="stepQuantity(1)">+</button>
                </div>
              </label>
              <label class="settings-toggle">
                <input v-model="grayscale" type="checkbox" class="settings-toggle__input" @change="onGrayscaleChange" />
                <span class="settings-switch" :class="{ 'is-on': grayscale }" aria-hidden="true">
                  <span class="settings-switch__knob" />
                </span>
                <span class="settings-toggle__copy">
                  <strong>Schwarzweiß</strong>
                </span>
              </label>
            </div>
          </div>
        </div>

        <!-- Upload (Single-Mode) als letztes -->
        <div v-if="showUpload" class="ac-item">
          <button class="ac-head" type="button" @click="openGroupFor({ id: '__upload__', title: 'Foto auswählen', icon: '', isSize: false, description: '' })">
            <span class="ac-head__left">
              <span class="ac-head__title">Foto auswählen</span>
              <span class="ac-head__val">{{ selectedImage }}</span>
            </span>
            <svg class="ac-head__chev" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
          </button>
        </div>

        </template><!-- end: accordion visible only when images selected -->

      <div v-if="isMulti && selectionIds.length > 0" class="inline-apply-row">
        <div v-if="isApplyingConfig" class="inline-apply-progress" aria-live="polite">
          <span class="inline-apply-progress__spinner" aria-hidden="true" />
          <span class="inline-apply-progress__text">
            Änderungen werden auf {{ applyingSelectionCount }} Bilder angewendet…
          </span>
        </div>
        <div v-else-if="touchedOptionGroupIds.size > 0" class="inline-apply-preview-hint" role="status">
          <strong>Nur Vorschau</strong> – noch nicht gespeichert. Erst mit <strong>Auswahl übernehmen</strong> werden die Änderungen gespeichert und in den Warenkorb übernommen.
        </div>
        <button
          class="btn btn--primary btn--full"
          type="button"
          @click="onAddToCart()"
          :disabled="!canSaveMulti || isApplyingConfig"
        >
          {{ isApplyingConfig ? 'Wird übernommen…' : 'Auswahl übernehmen' }}
        </button>
      </div>

      <!-- Preisleiste -->
      <div v-if="!isMulti" class="price-footer">
        <div class="price-meta">
          <div class="price-head">
            <small>
              Preis inkl. MwSt., zzgl.
              <a :href="shippingCostsHref" target="_blank" rel="noopener noreferrer">Versandkosten</a>
            </small>
            <strong class="price">
              <template v-if="priceLoading">…</template>
              <template v-else>{{ fmtCurrency ? fmtCurrency(price) : formatPriceFallback(selectedPrice * quantity) }}</template>
            </strong>
          </div>
          <div v-if="priceStale" class="price-stale-hint">
            <svg viewBox="0 0 16 16" width="13" height="13" fill="none" aria-hidden="true">
              <path d="M8 1.5a6.5 6.5 0 100 13 6.5 6.5 0 000-13zM8 5v3.5M8 10.5v.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
            Preis aktualisiert – Warenkorb erneuern
          </div>
          <div v-if="!props.controlled" class="price-controls single">
            <button class="btn" type="button" @click="onSaveSingle" :disabled="!canSaveSingle">Speichern</button>
            <button class="btn btn--primary" type="button" @click="onAddToCart()">In den Warenkorb</button>
            <button class="btn btn--cart-link" type="button" @click="goToCartLink" aria-label="Zum Warenkorb">
              <ShoppingCart :size="14" />
              <span>({{ cart.count }})</span>
            </button>
          </div>
          <p v-if="shippingNote" class="delivery-info">{{ shippingNote }}</p>
        </div>
      </div>
    </div>

    <!-- Detail -->
    <div
        class="view detail-view"
        v-show="!!activeGroupId"
    >
      <button class="back-btn" @click="closeGroup">
        <svg class="icon" viewBox="0 0 24 24">
          <path
              d="M15 6L9 12l6 6"
              stroke="#111"
              stroke-width="2"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
          />
        </svg>
        <span class="label">{{ activeGroupTitle || 'Zurück' }}</span>
      </button>

      <!-- SIZE -->
      <component
          v-if="activeComponentKey === 'size'"
          :is="GroupBildgroesse"
          v-model:selectedFormat="selectedFormat"
          v-model:selectedPrice="selectedPrice"
          :sizeOptions="sizeOptions"
          :imageMeta="imageMeta"
          :allow-custom="allowCustomSize"
          :live-price-label="!priceLoading && price > 0 ? (fmtCurrency ? fmtCurrency(price) : formatPriceFallback(price)) : ''"
          :live-price-loading="priceLoading"
          :limits="{
            minW: cfg?.product?.minSizeX ?? undefined,
            minH: cfg?.product?.minSizeY ?? undefined,
            maxW: cfg?.product?.maxSizeX ?? undefined,
            maxH: cfg?.product?.maxSizeY ?? undefined,
          }"
          :groupDescription="(effectiveDisplayGroups.find(g => g.id===activeGroupId)?.description) || ''"
          :diffs="sizeDiffs"
          :preselect-title="sizePreselectTitle"
          preselect-strategy="always"
          @changeSize="forwardSizeChange"
          @preview-size="previewCustomSizeChange"
          @size-clicked="onSingleSizeClicked"
          @close="closeGroup"
      />

      <!-- UPLOAD -->
      <GroupUpload
          v-else-if="activeComponentKey === 'upload' && showUpload"
          :category-id="props.category"
          :product-id="activeProductId ?? props.productId"
          :is-multi-mode="isMulti"
          :size-options="sizeOptions"
          :variant-groups="(cfg?.groups || []).filter((g:any) => !g?.isSize && g?.id !== '__upload__')"
          @uploaded="onImagesUploaded"
          @close="closeGroup"
      />

      <!-- PLACEHOLDER -->
      <GroupPlaceholder
          v-else-if="activeComponentKey === 'placeholder'"
          :group-id="activeGroupId || ''"
          :group-title="(effectiveDisplayGroups.find(g => g.id===activeGroupId)?.title) || ''"
          :group-description="(effectiveDisplayGroups.find(g => g.id===activeGroupId)?.description) || ''"
          :variants="(effectiveDisplayGroups.find(g => g.id===activeGroupId)?.options) || []"
          :selected-id="(effectiveDisplayGroups.find(g => g.id===activeGroupId)?.selected?.id) || ''"
          :diffs="diffsByGroup?.[activeGroupId || ''] || {}"
          :currency="currency"
          @select-variant="onSelectVariant"
          @close="closeGroup"
      />

      <div v-else>Unbekannte Gruppe.</div>
    </div>
  </div>

  <Teleport to="body">
    <div
      v-if="hoverPreview"
      class="hover-card"
      :style="{ left: `${hoverPreview.x}px`, top: `${hoverPreview.y}px` }"
    >
      <img
        v-if="hoverPreview.image"
        class="hover-card__img"
        :src="hoverPreview.image"
        :alt="hoverPreview.title"
        loading="lazy"
        decoding="async"
      />
      <div class="hover-card__body">
        <strong class="hover-card__title">{{ hoverPreview.title }}</strong>
        <p v-if="hoverPreview.description" class="hover-card__desc">{{ hoverPreview.description }}</p>
      </div>
    </div>
  </Teleport>

  <Teleport to="body">
    <div v-if="cartInfoModal" class="mini-dialog-backdrop" @click.self="cartInfoModal = null">
      <div class="mini-dialog" role="dialog" aria-modal="true" aria-label="Hinweis zum Warenkorb">
        <div class="mini-dialog__head">
          <strong>{{ cartInfoModal.title }}</strong>
          <button class="mini-dialog__close" type="button" @click="cartInfoModal = null" aria-label="Schließen">✕</button>
        </div>
        <div class="mini-dialog__body">
          <div class="mini-dialog__progress">
            <div class="mini-dialog__progress-meta">
              <span>Upload läuft</span>
              <span>{{ cartInfoModal.progress }}%</span>
            </div>
            <div class="mini-dialog__progress-track" aria-hidden="true">
              <span class="mini-dialog__progress-fill" :style="{ width: `${cartInfoModal.progress}%` }" />
            </div>
          </div>
          <p v-for="(line, idx) in cartInfoModal.body.split('\n\n')" :key="idx">{{ line }}</p>
        </div>
        <div class="mini-dialog__foot">
          <button class="btn" type="button" @click="openUploadQueueFromDialog">Upload-Status ansehen</button>
          <button class="btn btn--primary" type="button" @click="cartInfoModal = null">Verstanden</button>
        </div>
      </div>
    </div>
  </Teleport>

  <LeaveEditorConfirmModal
    :open="!!leaveConfirmModal"
    @close="leaveConfirmModal = null"
    @confirm="confirmLeaveEditor"
  />

</template>


<style scoped lang="scss">
/* Modern / Flat / Pixel-perfect Sidebar */

.sidebar-wrapper {
  /* Design Tokens */
  --bg: var(--pfe-color-bg, #ffffff);
  --surface: var(--pfe-color-bg, #ffffff);
  --surface-2: #fafafa;
  --border: var(--pfe-color-border, #ececec);
  --border-2: #e6e6e6;

  --text: var(--pfe-color-text, #141414);
  --muted: var(--pfe-color-muted, #6f6f6f);

  --brand: var(--pfe-color-accent, #e30b17);
  --brand-strong: var(--pfe-color-accent, #c80913);

  --radius: 4px;
  --radius-sm: 4px;
  --shadow: none;
  --shadow-soft: none;
  --pad: 14px;
  --gap: 8px;

  position: relative;
  z-index: 60;
  overflow-x: hidden;
  overflow-y: auto;
  width: 100%;
  max-width: none;
  height: 100%;
  background: var(--bg);
  box-sizing: border-box;
  min-width: 0;
  display: flex;
  flex-direction: column;

  @media (max-width: 767px) {
    height: 100%;
    overflow: hidden;
    width: 100%;
    max-width: 100%;
  }
}




/* Multi selection hint (Multi-Mode UX) */
/* ── Skeleton ── */
@keyframes sk-shimmer {
  0%   { background-position: -200% 0; }
  100% { background-position:  200% 0; }
}
.sk-sidebar {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0;
  overflow: hidden;
  flex: 1;
  user-select: none;
  pointer-events: none;
}
.sk-line {
  border-radius: 4px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: sk-shimmer 1.4s ease-in-out infinite;
}
/* Hint row */
.sk-hint {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid #f3f4f6;
}
.sk-pill {
  width: 36px; height: 20px; border-radius: 20px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: sk-shimmer 1.4s ease-in-out infinite;
}
/* Product section */
.sk-section {
  padding: 12px 14px 8px;
  border-bottom: 1px solid #f3f4f6;
}
.sk-label {
  width: 110px; height: 10px; border-radius: 4px; margin-bottom: 10px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: sk-shimmer 1.4s ease-in-out infinite;
}
.sk-product-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.sk-product-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
}
.sk-product-thumb {
  width: 44px; height: 44px; border-radius: 4px; flex-shrink: 0;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: sk-shimmer 1.4s ease-in-out infinite;
}
.sk-product-text { flex: 1; }
/* Option groups */
.sk-group {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px;
  border-bottom: 1px solid #f3f4f6;
}
.sk-group__icon {
  width: 22px; height: 22px; border-radius: 4px; flex-shrink: 0;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: sk-shimmer 1.4s ease-in-out infinite;
}
.sk-group__body { flex: 1; }
.sk-group__arrow {
  width: 8px; height: 14px; border-radius: 2px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: sk-shimmer 1.4s ease-in-out infinite;
}
/* Footer */
.sk-footer {
  margin-top: auto;
  padding: 16px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-top: 1px solid #f3f4f6;
}
.sk-btn-row {
  display: flex;
  gap: 8px;
  margin-top: 4px;
}
.sk-btn {
  flex: 1; height: 36px; border-radius: 4px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: sk-shimmer 1.4s ease-in-out infinite;
}
.sk-btn--primary {
  background: linear-gradient(90deg, #fca5a5 25%, #fecaca 50%, #fca5a5 75%);
  background-size: 200% 100%;
}
/* Overlay */
.sk-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(255,255,255,0.72);
  backdrop-filter: blur(3px);
}
.sk-overlay__text {
  font-size: 13px;
  line-height: 1.6;
  color: #374151;
  text-align: center;
  max-width: 200px;
  margin: 0;
  font-weight: 500;
}

.multi-hint {
  margin: 10px;
  padding: 12px 14px;
  border: 1px solid var(--border);
  background: var(--surface-2);
}
.multi-hint__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 6px;
}
.multi-hint__title {
  font-weight: 700;
  font-size: 14px;
  color: var(--text);
}
.multi-hint__pill {
  font-size: 13px;
  padding: 7px 11px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid var(--border);
  color: var(--text);
  white-space: nowrap;
}
.multi-hint__sub {
  font-size: 13px;
  color: var(--muted);
  line-height: 1.35;
}
.multi-hint__warn {
  font-size: 12px;
  line-height: 1.4;
  color: var(--text);
}
.multi-hint__warn strong { color: var(--brand-strong); }

.sidebar-logo {
  padding: 18px 14px 18px;
  border-bottom: 1px solid var(--border);
  display: flex;
  justify-content: center;
  align-items: center;
  background: #fff;
}
.sidebar-logo__link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}
.sidebar-logo__img {
  display: block;
  height: 38px;
  width: auto;
  max-width: 220px;
  object-fit: contain;
}

/* ── No-selection info screen ────────────────────── */
.no-sel {
  padding: 24px var(--pad) 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.no-sel__icon {
  color: var(--muted);
  opacity: 0.5;
  width: 58px;
  height: 58px;
  margin: 0 auto;
}
.no-sel__icon svg { width: 100%; height: 100%; }
.no-sel__title {
  font-size: 16px;
  font-weight: 700;
  color: var(--text);
  margin: 0;
  text-align: center;
  line-height: 1.3;
}
.no-sel__desc {
  font-size: 14px;
  color: var(--muted);
  margin: 0;
  line-height: 1.5;
  text-align: center;
}
.no-sel__steps {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.no-sel__steps li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
.no-sel__step-num {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--brand);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
}
.no-sel__step-text {
  font-size: 14px;
  color: var(--muted);
  line-height: 1.45;
}
.no-sel__step-text strong {
  color: var(--text);
  font-weight: 700;
}

/* Full-width button */
.btn--full { width: 100%; justify-content: center; }

/* Primary CTA variant */
.btn--primary {
  background: #e30b17;
  color: #fff;
  border: 1px solid #e30b17;
  font-weight: 700;
}
.btn--primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Views */
.view {
  position: relative;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  top: 0;
  left: 0;
  box-sizing: border-box;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;

  @media (max-width: 767px) {
    position: relative;
    width: 100%;
    overflow-y: auto;
    overscroll-behavior: contain;
  }
}

/* ── 2-column product card grid (Oberfläche) ──────────── */
.prod-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  padding: 10px var(--pad);
}
.prod-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  background: var(--surface-2);
  border: 1.5px solid var(--border);
  border-radius: 6px;
  padding: 8px 6px 7px;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
  text-align: center;
  position: relative;
}
.prod-card:hover {
  border-color: #bbb;
  box-shadow: 0 2px 8px rgba(0,0,0,.07);
}
.prod-card.active {
  border-color: var(--brand);
  background: #fff;
  box-shadow: 0 0 0 1px var(--brand);
}
.prod-card__img-wrap {
  position: relative;
  width: 100%;
  aspect-ratio: 3 / 2;
  border-radius: 4px;
  overflow: hidden;
  background: #f0f0f0;
}
.prod-card__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.prod-card__check {
  position: absolute;
  top: 4px;
  right: 4px;
  background: var(--brand);
  color: #fff;
  border-radius: 50%;
  padding: 2px;
  stroke: #fff;
}
.prod-card__name {
  font-size: 14px;
  font-weight: 600;
  color: var(--text);
  line-height: 1.3;
  word-break: break-word;
}

/* Back Button */
.back-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;

  background: transparent;
  border: 0;
  cursor: pointer;
  padding: 10px 0;
  margin-bottom: 18px;

  .icon {
    width: 20px;
    height: 20px;
    flex: 0 0 auto;
    opacity: 0.9;
  }

  .label {
    font-weight: 700;
    text-transform: uppercase;
    color: var(--text);
    font-size: 18px;
    line-height: 1;
  }

  &:hover .label {
    opacity: 0.9;
  }

  &:focus-visible {
    outline: 3px solid rgba(227, 11, 23, 0.25);
    outline-offset: 4px;
    border-radius: 0;
  }
}

/* ─── Inline Produkt-Auswahl ──────────────────────────────────── */
/* Groups List spacing */
.overview-view {
  padding: 12px 0 16px;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

/* ── Accordion ──────────────────────────────────────── */
.ac-item {
  border-bottom: 1px solid var(--border);
}
.ac-item:first-child { border-top: 1px solid var(--border); }

/* Primary product-selection items (Produkte, Oberfläche) stand out */
.ac-item--primary .ac-head {
  padding: 16px var(--pad);
}
.ac-item--primary .ac-head__title {
  font-size: 15px;
  font-weight: 700;
}

.ac-head {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 14px var(--pad);
  background: transparent;
  border: 0;
  cursor: pointer;
  text-align: left;
  transition: background 0.1s;
  &:hover { background: var(--surface-2); }
}
.ac-head__icon {
  width: 42px;
  height: 42px;
  object-fit: cover;
  border-radius: 4px;
  flex-shrink: 0;
  border: 0;
  background: transparent;
}
.ac-head__icon--primary {
  width: 42px;
  height: 42px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid var(--border);
}
.ac-head__left {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
}
.ac-head__title {
  font-size: 15px;
  font-weight: 600;
  color: var(--text);
  line-height: 1.2;
}
.ac-head__val {

  font-size: 15px; font-weight: 500;
  color: var(--muted);
  line-height: 1.25;
}
.ac-head__chev {
  flex-shrink: 0;
  color: var(--muted);
  transition: transform 0.18s ease;
  &.open { transform: rotate(180deg); }
}

.ac-body {
  border-top: 1px solid var(--border);
  max-height: 240px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(15, 23, 42, 0.14) transparent;
}

/* Größenliste: komplett ausklappen, der Sidebar-Scroll übernimmt. */
.ac-body--sizes {
  max-height: none;
  overflow-y: visible;
}

/* Option rows inside accordion body (subcats, products, variants) */
.ac-opt {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px var(--pad);
  background: transparent;
  border: 0;
  border-bottom: 1px solid var(--border);
  cursor: pointer;
  text-align: left;
  transition: background 0.1s;
  &:last-child { border-bottom: 0; }
  &:hover { background: var(--surface-2); }
  &.active { background: rgba(227, 11, 23, 0.05); }
}
.ac-opt__thumb {
  width: 50px;
  height: 50px;
  object-fit: cover;
  border: 1px solid var(--border);
  background: var(--surface-2);
  display: block;
  border-radius: 2px;
}
.ac-opt__name {
  font-size: 14px;
  font-weight: 500;
  color: var(--text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}
.ac-head__surcharge { font-weight: 700; font-variant-numeric: tabular-nums; }

.ac-opt__delta {
  margin-left: auto;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--muted, #6b7280);
}
.ac-opt__delta.up { color: #c73737; }
.ac-opt__delta.down { color: #2f7d4f; }
.ac-opt__name { flex: 0 1 auto; }

.ac-opt__check {
  color: var(--brand);
  flex-shrink: 0;
}

.ac-body--settings {
  padding: 0;
}
.settings-card {
  display: grid;
  gap: 14px;
  padding: 14px var(--pad);
  background: #fff;
}
.settings-field {
  display: grid;
  gap: 7px;
}
.settings-field__label {
  font-size: 12px;
  font-weight: 700;
  color: var(--text);
}
.settings-qty {
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr) 42px;
  border: 1px solid var(--border);
  background: #fff;
}
.settings-qty__step {
  border: 0;
  background: #fff;
  font-size: 18px;
  color: var(--text);
  cursor: pointer;
}
.settings-qty__step + .settings-qty__input,
.settings-qty__input + .settings-qty__step {
  border-left: 1px solid var(--border);
}
.settings-qty__input {
  width: 100%;
  min-width: 0;
  border: 0;
  background: #fff;
  text-align: center;
  font-size: 14px;
  font-weight: 700;
  color: var(--text);
  height: 42px;
  appearance: textfield;
  -moz-appearance: textfield;
}
.settings-qty__input::-webkit-outer-spin-button,
.settings-qty__input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.settings-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 0;
  cursor: pointer;
}
.settings-toggle__input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}
.settings-switch {
  width: 38px;
  height: 22px;
  border-radius: 999px;
  background: #d1d5db;
  position: relative;
  flex: 0 0 auto;
  transition: background .18s ease;
}
.settings-switch.is-on {
  background: #111827;
}
.settings-switch__knob {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 18px;
  height: 18px;
  border-radius: 999px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.15);
  transition: transform .18s ease;
}
.settings-switch.is-on .settings-switch__knob {
  transform: translateX(16px);
}
.settings-toggle__copy {
  display: flex;
  align-items: center;
}
.settings-toggle__copy strong {
  font-size: 13px;
  color: var(--text);
}

/* Format rows (size list inside accordion body) */
.fmt-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px var(--pad);
  border: 0;
  border-bottom: 1px solid var(--border);
  background: transparent;
  cursor: pointer;
  text-align: left;
  width: 100%;
  transition: background 0.1s;
  &:last-child { border-bottom: 0; }
  &:hover { background: var(--surface-2); }
  &.active {
    background: rgba(227, 11, 23, 0.05);
    border-left: 3px solid var(--brand);
    padding-left: calc(var(--pad) - 3px);
  }
}
.fmt-row__name {
  font-size: 14px;
  font-weight: 500;
  color: var(--text);
  flex: 1;
  min-width: 0;
  /* Größenbezeichnung nie abschneiden: bei schmaler Sidebar umbrechen. */
  white-space: normal;
  overflow-wrap: anywhere;
}
.fmt-row__price {
  font-size: 13px;
  color: var(--muted);
  white-space: nowrap;
  flex-shrink: 0;
  .fmt-row.active & { color: var(--brand); font-weight: 600; }
}
.ac-head__regular {
  margin-left: 6px;
  color: var(--muted);
  font-weight: 400;
}
.fmt-row__price.is-sale { color: var(--brand); font-weight: 600; }
.fmt-row__regular {
  margin-right: 4px;
  color: var(--muted);
  font-weight: 400;
}
.sale-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 999px;
  background: var(--brand);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  vertical-align: middle;
}
/* Mit Preis daneben: Produktname umbrechen statt abschneiden ("Matt"/"Glanz" muss lesbar bleiben). */
.ac-opt:has(.ac-opt__price) .ac-opt__name {
  white-space: normal;
  overflow: visible;
  flex: 1 1 auto;
}
.ac-opt__price {
  margin-left: auto;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--muted, #6b7280);
  white-space: nowrap;
  .sale-badge { margin: 0 6px 0 0; }
  &.is-sale { color: var(--brand); }
}
.fmt-custom {
  border-top: 1px solid var(--border);
  background: linear-gradient(180deg, #fcfcfd 0%, #f8fafc 100%);
}
.fmt-custom__toggle {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px var(--pad);
  border: 0;
  background: transparent;
  cursor: pointer;
  text-align: left;
}
.fmt-custom__toggle.active {
  background: rgba(227, 11, 23, 0.04);
}
.fmt-custom__toggle-copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.fmt-custom__toggle-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--text);
}
.fmt-custom__toggle-sub {
  font-size: 11px;
  color: var(--muted);
}
.fmt-custom__toggle-switch {
  width: 36px;
  height: 22px;
  border-radius: 999px;
  background: #d0d5dd;
  position: relative;
  flex-shrink: 0;
  transition: background .18s ease;
}
.fmt-custom__toggle-switch.on {
  background: #111827;
}
.fmt-custom__toggle-knob {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.15);
  transition: transform .18s ease;
}
.fmt-custom__toggle-switch.on .fmt-custom__toggle-knob {
  transform: translateX(14px);
}
.fmt-custom__panel {
  padding: 0 var(--pad) 12px;
}
.fmt-custom__sub {
  margin-top: 3px;
  font-size: 11px;
  color: var(--muted);
}
.fmt-custom__fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-top: 10px;
}
.fmt-custom__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.fmt-custom__field span {
  font-size: 11px;
  font-weight: 600;
  color: var(--muted);
}
.fmt-custom__field input {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  height: 34px;
  border: 1px solid var(--border);
  background: #fff;
  padding: 0 10px;
  font-size: 14px;
  color: var(--text);
}
.fmt-custom__hint {
  margin-top: 8px;
  font-size: 11px;
  color: var(--muted);
}
.fmt-custom__error {
  margin-top: 8px;
  font-size: 11px;
  color: #b42318;
}
.fmt-custom__price {
  margin-top: 10px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border: 1px solid var(--border);
  background: rgba(255,255,255,0.75);
}
.fmt-custom__price-label {
  font-size: 11px;
  font-weight: 600;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: .02em;
}
.fmt-custom__price-value {
  font-size: 14px;
  font-weight: 700;
  color: var(--text);
}
.fmt-custom__apply-hint {
  margin-top: 10px;
  font-size: 11px;
  color: var(--muted);
}

/* Price Footer – sticky am unteren Rand der Sidebar */
.price-footer {
  position: sticky;
  bottom: 0;
  z-index: 10;
  background: #fff;
  margin: auto 10px 10px 10px;
  border-top: 1px solid var(--border);
  min-width: 0;
}

.inline-apply-row {
  padding: 10px 10px 0;
  display: grid;
  gap: 8px;
  position: sticky;
  bottom: 0;
  background: #fff;
}

@media (max-width: 767px) {
  .inline-apply-row {
    position: sticky;
    bottom: 0;
    z-index: 14;
    margin-top: auto;
    padding: 12px 12px calc(12px + env(safe-area-inset-bottom, 0px));
    border-top: 1px solid var(--border);
    background: rgba(255, 255, 255, 0.98);
    backdrop-filter: blur(8px);
  }
}

.inline-apply-row .btn {
  min-height: 40px;
  font-size: 14px;
  font-weight: 700;
}

.inline-apply-progress {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(248, 250, 252, 0.96);
  color: #475569;
  font-size: 12px;
  line-height: 1.35;
}

.inline-apply-progress__spinner {
  width: 14px;
  height: 14px;
  border-radius: 999px;
  border: 2px solid rgba(226, 232, 240, 0.95);
  border-top-color: #e30b17;
  animation: sidebar-apply-spin .85s linear infinite;
  flex: 0 0 auto;
}

.inline-apply-progress__text {
  display: block;
}

.inline-apply-preview-hint {
  padding: 9px 11px;
  border: 1px solid rgba(217, 119, 6, 0.35);
  border-radius: 6px;
  background: #fffbeb;
  color: #78350f;
  font-size: 12px;
  line-height: 1.4;
}

@keyframes sidebar-apply-spin {
  to { transform: rotate(360deg); }
}

.price-meta small {
  display: inline-block;
  font-size: 12px;
  color: var(--muted);
  line-height: 1.2;

  a {
    color: var(--brand);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

.price-head {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
  margin: 6px 0 6px;
  min-width: 0;
}

.price-total-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 5px 0 5px;
  border-top: 1px solid var(--border);
  margin-bottom: 6px;
  min-width: 0;
}
.price-total-label {
  font-size: 12px;
  color: var(--muted);
  white-space: nowrap;
}
.price-total-value {
  font-size: 15px;
  font-weight: 700;
  color: var(--text);
  white-space: nowrap;
}

.price-controls {
  display: grid;
  grid-template-columns: 40% 40% 20%;
  align-items: center;
  gap: 4px;
  margin: 0 0 4px;
  min-width: 0;
}

.price-controls .btn {
  height: 40px;
  font-size: 16px;
  border-radius: 2px;
}

.btn {
  appearance: none;
  border: 1px solid var(--border);
  border-radius: 0;

  padding: 0 8px;
  font-weight: 600;
  height: 32px;
  font-size: 11px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  background: #fff;
  color: var(--text);

  cursor: pointer;
  transition: transform 0.08s ease, background 0.15s ease;

  &:hover {
    background: var(--surface-2);
  }

  &:active {
    transform: translateY(1px);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  &:focus-visible {
    outline: 3px solid rgba(227, 11, 23, 0.28);
    outline-offset: 2px;
  }
}
.btn--primary {
  background: #e30b17;
  color: #fff;
  border: 1px solid #e30b17;
}
.btn--primary:hover {
  background: #c80913;
  border-color: #c80913;
}

.price {
  margin-left: auto;
  font-size: 18px;
  font-weight: 700;
  color: var(--text);
  white-space: nowrap;
  line-height: 1;
  padding-left: 4px;
  min-width: 0;
}

.delivery-info {
  font-size: 12px;
  color: var(--muted);
  margin: 2px 0 0;
}

.btn--cart-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.hover-card {
  position: fixed;
  z-index: 10050;
  width: min(360px, calc(100vw - 24px));
  border: 1px solid var(--border);
  border-radius: 10px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 18px 42px rgba(15, 23, 42, 0.18);
  pointer-events: none;
  backdrop-filter: blur(8px);
}

.hover-card__img {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 10;
  object-fit: cover;
  background: var(--surface-2);
}

.hover-card__body {
  display: grid;
  gap: 6px;
  padding: 14px 15px 15px;
}

.hover-card__title {
  font-size: 15px;
  font-weight: 700;
  line-height: 1.2;
  color: var(--text);
}

.hover-card__desc {
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
  color: var(--muted);
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.mini-dialog-backdrop {
  position: fixed;
  inset: 0;
  z-index: 10060;
  background: rgba(15, 23, 42, 0.34);
  display: grid;
  place-items: center;
  padding: 16px;
}

.mini-dialog {
  width: min(520px, 100%);
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 6px;
  box-shadow: 0 26px 60px rgba(15, 23, 42, 0.22);
  overflow: hidden;
}

.mini-dialog__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px;
  border-bottom: 1px solid var(--border);
}

.mini-dialog__head strong {
  font-size: 18px;
  line-height: 22px;
  color: var(--accent, #e30b17);
}

.mini-dialog__close {
  width: 34px;
  height: 34px;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: #fff;
  color: var(--text);
}

.mini-dialog__body {
  padding: 18px;
  display: grid;
  gap: 10px;
}

.mini-dialog__progress {
  display: grid;
  gap: 8px;
}

.mini-dialog__progress-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 12px;
  line-height: 16px;
  color: var(--muted);
}

.mini-dialog__progress-track {
  height: 8px;
  border-radius: 999px;
  background: #eceef3;
  overflow: hidden;
}

.mini-dialog__progress-fill {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, var(--accent, #e30b17), #f36a72);
}

.mini-dialog__body p {
  margin: 0;
  font-size: 14px;
  line-height: 22px;
  color: var(--muted);
}

.mini-dialog__foot {
  padding: 0 18px 18px;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.mini-dialog__foot .btn {
  height: 40px;
  padding: 0 14px;
  font-size: 14px;
  line-height: 14px;
  font-weight: 700;
  border-radius: 3px;
}

</style>
